import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";

type Slot = "A" | "B";

export interface VoTrack {
  id?: string;
  url: string;
  startTime: number;
  duration?: number;
  volume?: number;
  duckVideoTo?: number;
  /**
   * Optional offset INSIDE the source file where playback starts — the
   * head-trim in-point. Defaults to 0 (play the file from its beginning),
   * which is what the render worker does today (`atrim=start=0`), so this
   * stays 0 until the worker accepts an in-point.
   */
  sourceIn?: number;
}

interface VoRuntime {
  track: VoTrack;
  endTime: number;
  active: boolean;
  el?: HTMLAudioElement;
}

/**
 * One sound effect on the timeline.
 *
 * Effects do not go through an `<audio>` element like voice and music.
 * They are short, so they are decoded once into memory and started with
 * Web Audio, which begins a sound when asked rather than tens of
 * milliseconds later — the difference between a hit landing on the frame
 * and landing near it. See docs/sfx-library-plan.md §7.
 */
export interface SfxTrack {
  id: string;
  url: string;
  /** Timeline second the effect fires at. */
  startTime: number;
  /** How long it is heard for. */
  duration: number;
  volume?: number;
  /** Seconds into the file to start from (head trim). */
  sourceIn?: number;
  /** Repeat the sound to fill `duration` when the bar is longer than the
   *  file. Set by the editor when you stretch an effect past its length,
   *  the same rule music follows. */
  loop?: boolean;
}

export interface BgmTrack {
  url: string;
  volume?: number;
  loop?: boolean;
  duckedByVoTo?: number;
  /**
   * Optional render-timeline second when BGM playback should begin.
   * Defaults to 0 (BGM plays from the start of the render).
   */
  startTime?: number;
  /**
   * Optional render-timeline second when BGM playback should end.
   * Defaults to "play to the end of the render" (no cap).
   */
  endTime?: number;
  /** Head-trim in-point inside the source file. See VoTrack.sourceIn. */
  sourceIn?: number;
}

interface BgmRuntime {
  track: BgmTrack;
  duration: number;          // probed source duration in seconds
  active: boolean;           // currently inside its render-timeline window
  el?: HTMLAudioElement;
}

/**
 * Timeline annotation pin (e.g. a feedback timecode marker).
 * Structurally compatible with the videojs-player's VideoAnnotation so
 * the workspace `videoAnnotations` getter can feed either player.
 */
export interface VideoAnnotation {
  id: string | number;
  time: number;
  label: string;
  color?: string;
}

/**
 * Spatial overlay drawn on top of the video frame. x/y are percentages
 * of the visible video surface; startTime/endTime are timeline-global
 * seconds (so render and non-render versions use the same coordinate
 * space).
 */
export interface VideoOverlay {
  id: string | number;
  startTime: number;
  endTime: number;
  x: number;
  y: number;
  shape: "circle" | "rectangle" | "arrow";
  color?: string;
  size?: number;
  label?: string;
  /**
   * Numeric id of the user who created this overlay. When set, only that
   * user can edit / delete / move it. Leave undefined for legacy data so
   * older overlays without an author stay editable by anyone.
   */
  createdBy?: number | string | null;
  /** Display name of the creator (e.g. "Avinash null"). Used in the chip. */
  createdByName?: string | null;
  /** ISO timestamp of creation. Rendered as relative time in the chip. */
  createdAt?: string | null;
}

/**
 * A piece of on-screen text (a "super", spec §12) drawn over the frame
 * for the seconds it is on. Position and size are shares of the visible
 * frame so the same values work at any window size and in every ratio.
 */
export interface TextOverlay {
  id: string;
  text: string;
  startTime: number;
  endTime: number;
  /** The point the text is placed against, as a share of the frame
   *  (0..100). WHICH part of the box sits on that point is `anchorX` /
   *  `anchorY` — for a corner preset it is that corner, not the centre. */
  xPct: number;
  yPct: number;
  /** Which edge of the text box the point holds.
   *
   *  A left-hand preset puts the box's LEFT edge on the point, a
   *  right-hand one its right edge, and a centred one its middle. Placing
   *  every preset by its centre is what put a wide caption off the side
   *  of the frame: "top left" sits at 12%, and half of a box wider than
   *  24% of the frame has nowhere to go but outside it.
   *
   *  Dragged text is centred on the point, which is what dragging means. */
  anchorX?: "start" | "center" | "end";
  anchorY?: "start" | "center" | "end";
  /** Cap on the box width, as a share of the frame width. */
  widthPct?: number;
  /** Text height as a share of the frame HEIGHT, so it scales with the
   *  frame rather than the window. */
  sizePct: number;
  color?: string;
  /** Base opacity as a percentage (style.opacity × 100). */
  opacityPct?: number;
  align?: "left" | "center" | "right";
  /** Readability backing (spec §12.2). */
  shadow?: boolean;
  box?: boolean;
  /** How it comes in and goes out, in the shared FE/BE contract spelling
   *  (docs/text-overlay-title-card-contract.md §6). */
  animation?: {
    in?: TextAnimSpec;
    out?: TextAnimSpec;
  };
}

export interface TextAnimSpec {
  /** none | fade | slide_left | slide_right | slide_up | slide_down |
   *  typewriter (the Phase 1 set; others fall back to none). */
  type?: string;
  duration?: number;
  delay?: number;
  easing?: "linear" | "ease_in" | "ease_out" | "ease_in_out" | string;
  typewriter?: { charactersPerSecond?: number };
}

/**
 * A title card slide: a stretch of the cut with no video file, drawn from
 * its settings (shared FE/BE contract, `title_card`). Passed per clip index
 * — null for a normal clip.
 */
/** Fade in from black / fade out to black on one clip, in seconds
 *  (Preview spec §10.1). 0 means no fade. */
export interface ClipFadeSpec {
  in: number;
  out: number;
}

/** How a join is made (spec §10.2), in the shared enum. `cut` is the
 *  absence of a transition, so it never reaches the player. */
export type TransitionKind =
  | "crossfade"
  | "dip_to_black"
  | "dip_to_white"
  | "slide_left"
  | "slide_right"
  | "slide_up"
  | "slide_down"
  | "wipe_left"
  | "wipe_right"
  | "wipe_up"
  | "wipe_down";

/** The transition on a join, as the player needs it.
 *  - A **dip** darkens (or whitens) the tail of one clip and the head of
 *    the next, half each, and needs only one picture on screen.
 *  - A **blend** (crossfade, slide, wipe) needs both pictures at once: the
 *    incoming clip is pre-rolled in the spare slot so that it reaches its
 *    own in-point exactly at the cut. Nothing about the timing changes
 *    either way — the cut keeps its length and nothing moves (§10.3).
 *  `blendable: false` means one side has no picture to blend with (a title
 *  card, or a file that isn't there), so the player dips instead. */
export interface JoinTransition {
  kind: TransitionKind;
  duration: number;
  blendable?: boolean;
}

export interface CardSlide {
  /** The flat background: a colour, or a CSS gradient. */
  backgroundColor?: string;
  /** A picture drawn over the flat background, when the card has one. */
  backgroundImageUrl?: string | null;
  backgroundFit?: "cover" | "contain" | "stretch";
  /** A mark in one corner. Position and size come as shares of the frame. */
  logo?: {
    url: string;
    xPct: number;
    yPct: number;
    widthPct: number;
    opacity: number;
  } | null;
  title?: string;
  subtitle?: string;
  body?: string;
  cta?: string;
  color?: string;
  /** Title height as a share of the frame height. */
  titleSizePct?: number;
  alignment?: "left" | "center" | "right";
  /** How the card comes in and goes out (contract `cardAnimation`):
   *  none | fade | slide_left | slide_right | slide_up | slide_down |
   *  zoom_in (in) / zoom_out (out). */
  animation?: { in?: TextAnimSpec; out?: TextAnimSpec };
}

@Component({
  selector: "app-multi-video-player",
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: "./multi-video-player.component.html",
  styleUrl: "./multi-video-player.component.scss",
})
export class MultiVideoPlayerComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  @Input() videoUrls: string[] = [];
  /**
   * Per-clip volume overrides, indexed parallel to videoUrls. Values are
   * 0..1. Entries that are missing or null default to full volume (1).
   * Mute a single clip by setting its slot to 0.
   */
  @Input() clipVolumes: (number | null | undefined)[] = [];
  /**
   * Per-clip trim window (in/out points in source-clip seconds), indexed
   * parallel to videoUrls. When set, the player seeks to `in` whenever it
   * loads that slot and advances to the next clip once playback reaches
   * `out`. Absent entries (or null) mean "play the whole source clip" —
   * equivalent to `{in: 0, out: rawDuration}`.
   */
  @Input() clipTrims: ({ in: number; out: number } | null | undefined)[] = [];
  /**
   * Per-clip availability — indexed parallel to videoUrls. When an entry has
   * `available: false` the player covers the frame with an opaque "clip no
   * longer available" overlay and forces that clip's volume to 0 (regardless
   * of master mute or clip volume) so neither the visual nor the audio of
   * the source leaks through. The clip's slot still counts toward timeline
   * duration so timing/ordering of audio + subsequent clips is preserved.
   * Absent entries (or null) mean "available" — default behavior.
   */
  @Input() clipAvailability: ({ available: boolean; reason: string | null } | null | undefined)[] = [];
  /**
   * Per-clip AI Edit job state, indexed parallel to videoUrls. Drives the
   * "Editing…" / "Edit failed" overlay shown ON the player itself when the
   * currently-playing clip is mid-edit or the last attempt errored.
   * Absent entries (or null) mean "no job state" — default behavior.
   */
  @Input() clipEditStates: ({ isEditing?: boolean; isEditError?: boolean; editErrorMessage?: string | null } | null | undefined)[] = [];
  /**
   * Server-provided source duration per clip (seconds), indexed parallel to
   * videoUrls. Used as a fallback whenever the player's URL probe fails —
   * specifically for unavailable clips whose signed URLs may 404. Without
   * this the timeline would treat such a clip as 0s; with it, the timeline
   * still reserves the full duration and the player auto-skips after that
   * many seconds (since the actual <video> can't fire `ended`).
   */
  @Input() clipFallbackDurations: number[] = [];
  /**
   * Title cards, indexed parallel to `videoUrls`. An entry means that slot
   * has no video file: the player draws the card and uses the same timer
   * path as an unavailable clip to carry the playhead through it.
   */
  @Input() cardSlides: (CardSlide | null | undefined)[] = [];
  /** Fades per slot, parallel to `videoUrls` (spec §10.1). */
  @Input() clipFades: (ClipFadeSpec | null | undefined)[] = [];
  /** The transition on the join AFTER each slot (spec §10.2). */
  @Input() transitions: (JoinTransition | null | undefined)[] = [];
  @Input() voTracks: VoTrack[] = [];
  /** Legacy single-BGM input — still honored. Prefer `bgmTracks` (array)
   *  for multi-BGM timelines; when both are supplied, `bgmTracks` wins. */
  @Input() bgmTrack: BgmTrack | null = null;
  /** Multiple BGM tracks, each with its own startTime/endTime window.
   *  Render-timeline-scheduled the same way as voTracks. */
  @Input() bgmTracks: BgmTrack[] = [];
  @Input() sfxTracks: SfxTrack[] = [];
  @Input() defaultDuckTo: number | null = null;
  @Input() duckFadeMs = 200;
  @Input() annotations: VideoAnnotation[] = [];
  @Input() overlays: VideoOverlay[] = [];
  /**
   * Currently signed-in user. Used to gate overlay edit/delete/move to
   * the author. Pass `null` (default) to disable ownership checks (e.g.
   * for the demo route or anonymous viewers).
   */
  @Input() currentUserId: number | string | null = null;
  /** When true, hides the Annotate button and blocks overlay creation. */
  @Input() readOnly: boolean = false;

  /**
   * Controls visibility of EVERY scrub-bar overlay: clip boundaries, VO
   * windows, and feedback annotation pins. Hidden by default so the
   * seeker stays clean for clients watching a cut; editors flip it on
   * via the markers button or the `M` keyboard shortcut when they need
   * to see where transitions / notes land.
   */
  showTimelineMarkers = false;

  /** Fires on every frame/timeupdate with the timeline-global seconds. */
  @Output() videoTimeUpdate = new EventEmitter<number>();
  /** Fires when the user clicks an annotation pin on the timeline. */
  @Output() annotationClick = new EventEmitter<VideoAnnotation>();
  /** Fires once when the player transitions from paused to playing. */
  @Output() videoPlay = new EventEmitter<void>();
  /** Fires once when the player transitions from playing to paused. */
  @Output() videoPause = new EventEmitter<void>();
  /** Fires after a seek lands, with the new timeline-global seconds. */
  @Output() videoSeeked = new EventEmitter<number>();
  /** Fires when a saved overlay is clicked. */
  @Output() overlayClick = new EventEmitter<VideoOverlay>();
  /** Fires when the user finishes creating a new overlay. */
  @Output() overlayAdd = new EventEmitter<VideoOverlay>();
  /** Fires when the user clicks the × button on an existing overlay. */
  @Output() overlayDelete = new EventEmitter<VideoOverlay>();
  /** Fires after the user drag-repositions an existing overlay. */
  @Output() overlayUpdate = new EventEmitter<VideoOverlay>();

  // ─── Overlay creation / edit state ────────────────────────────────────
  isAddingOverlay = false;
  showOverlayForm = false;
  newOverlay: Partial<VideoOverlay> = {};
  /** When set, the form is editing this existing overlay (not creating). */
  editingOverlayId: string | number | null = null;
  readonly overlayColors: string[] = [
    "#00F0FF",
    "#FF9E1B",
    "#00D68F",
    "#FF4757",
    "#A55EEA",
  ];

  // Drag state for the floating creation panel. `formPos` null = default
  // dock (bottom-right via CSS). Once the user drags, we switch to absolute
  // left/top coords. Reset on cancel/save so the panel docks again next time.
  formPos: { left: number; top: number } | null = null;
  private dragging = false;
  private dragStart = { mouseX: 0, mouseY: 0, left: 0, top: 0 };

  // Drag state for repositioning existing overlays. We mutate the overlay
  // object in place during the drag so the binding updates without a new
  // input reference. `overlayDragMoved` lets us suppress the click-to-seek
  // when the mousedown turned into an actual drag.
  draggingOverlayId: string | number | null = null;
  private overlayDragStart = { mouseX: 0, mouseY: 0, x: 0, y: 0 };
  private overlayDragMoved = false;
  private draggedOverlay: VideoOverlay | null = null;
  private readonly OVERLAY_DRAG_THRESHOLD_PX = 3;

  // Overlay size is stored as "pixels at OVERLAY_REFERENCE_WIDTH_PX of
  // wrapper width". At any other display width we scale by `overlayScale`
  // so the overlay covers the same fraction of the video frame regardless
  // of fullscreen / window-size changes.
  private static readonly OVERLAY_REFERENCE_WIDTH_PX = 720;
  overlayScale = 1;
  private overlayResizeObserver: ResizeObserver | null = null;

  @ViewChild("videoA") videoARef!: ElementRef<HTMLVideoElement>;
  @ViewChild("videoB") videoBRef!: ElementRef<HTMLVideoElement>;
  @ViewChild("videoWrapper") videoWrapperRef!: ElementRef<HTMLDivElement>;
  /** The black/white dip for fades and transitions. Painted straight on
   *  the element (see tickDip) so it moves at frame rate. */
  @ViewChild("videoDim") private videoDimRef?: ElementRef<HTMLDivElement>;
  @ViewChildren("voAudio") voAudioRefs!: QueryList<
    ElementRef<HTMLAudioElement>
  >;
  @ViewChildren("bgmAudio") bgmAudioRefs!: QueryList<
    ElementRef<HTMLAudioElement>
  >;

  bgmRuntimes: BgmRuntime[] = [];
  private bgmProbeToken = 0;

  voRuntimes: VoRuntime[] = [];

  // ─── Sound effects: decoded once, started on the frame ───────────────
  //
  // Everything else in this player runs on media elements. Effects do not,
  // for one reason: `<audio>.play()` is a request, not an instruction, and
  // the sound arrives tens of milliseconds later. That is inaudible under
  // narration and obvious on an impact cut to a visual hit.
  //
  // The cost is a second clock — effects run on `AudioContext.currentTime`
  // while picture and the other audio run on the media elements. They can
  // drift apart, so nothing here holds a sound open across that gap: each
  // one is started, plays for at most its own length (a second or two),
  // and is stopped outright on pause, seek and reset. Drift within a sound
  // that short is not audible; only its start matters.
  private audioCtx: AudioContext | null = null;
  /** Decoded audio, keyed by url. Effects are small and are reused across
   *  a timeline, so this is bounded by how many distinct sounds a cut
   *  uses, not by how many times they are placed. */
  private sfxBuffers = new Map<string, AudioBuffer>();
  private sfxLoading = new Set<string>();
  /** Sounds playing right now, so a pause can stop them. */
  private sfxLive = new Map<string, AudioBufferSourceNode>();
  /** Effects already fired for the current pass over their window, so a
   *  sound starts once rather than on every tick it is inside. */
  private sfxFired = new Set<string>();

  private voProbeToken = 0;
  private readonly DRIFT_HARD_RESYNC_SEC = 0.1;
  private fadeStateByEl = new Map<
    HTMLMediaElement,
    { target: number; handle: number | null }
  >();

  currentIndex = 0;
  activeSlot: Slot = "A";
  slotAUrl: string | null = null;
  slotBUrl: string | null = null;

  isPlaying = false;
  isFullscreen = false;
  playbackRate = 1;
  readonly playbackRates: number[] = [0.5, 1, 1.5, 2];

  masterVolume = 1;
  isMuted = false;

  /**
   * Raw source durations from <video>.duration — set once per probe pass.
   * Independent of trims; used as the hard ceiling when computing the
   * effective per-clip window.
   */
  clipRawDurations: number[] = [];
  /**
   * Effective per-clip durations on the assembled timeline (i.e. `out -
   * in`). Falls back to the raw duration when no trim is set. This is what
   * external callers should reason about when displaying / snapping.
   */
  clipDurations: number[] = [];
  cumulativeOffsets: number[] = [];
  totalDuration = 0;
  metadataReady = false;
  // Per-clip probing progress for the "Preparing render…" overlay.
  // Increments after each successful <video>.duration probe.
  probedClipCount = 0;

  currentTime = 0;
  progress = 0;

  private probeToken = 0;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["sfxTracks"]) {
      // Decode ahead of the playhead. Reaching a sound that has not been
      // decoded yet means skipping it for this pass — the window is only
      // a second or two wide and a fetch does not fit inside it.
      this.stopAllSfx();
      for (const t of this.sfxTracks || []) void this.loadSfx(t.url);
    }
    if (changes["videoUrls"]) {
      this.reset();
      if (this.videoUrls?.length) {
        this.probeDurations();
      }
    }
    if (changes["voTracks"]) {
      // Hot-swap when only metadata changed (volume / start / duration),
      // not the track set. Full rebuild tears down the <audio> elements +
      // pauseAllVoAudio resets currentTime, which glitches the audio on
      // every slider tick of a VO volume drag. Hot-swap keeps playback
      // continuous; syncVo applies the new volume on the next tick.
      const prev = changes["voTracks"].previousValue as VoTrack[] | undefined;
      const next = this.voTracks ?? [];
      const sameSet =
        !!prev &&
        prev.length === next.length &&
        this.voRuntimes.length === next.length &&
        prev.every((p, i) => p.url === next[i]?.url);
      if (sameSet) {
        const mul = this.effectiveMaster();
        for (let i = 0; i < next.length; i++) {
          const track = next[i];
          const rt = this.voRuntimes[i];
          rt.track = track;
          rt.endTime =
            track.duration != null
              ? track.startTime + track.duration
              : Number.POSITIVE_INFINITY;
          // Push volume change to any active audio element right away so
          // the user hears the slider drag instantly (syncVo handles the
          // same on every rVFC tick during playback, but a paused user
          // dragging the slider wouldn't get a tick).
          if (rt.active && rt.el) {
            const tv = (track.volume ?? 1) * mul;
            try {
              rt.el.volume = Math.max(0, Math.min(1, tv));
            } catch {}
          }
        }
      } else {
        this.rebuildVoRuntimes();
        this.probeVoDurations();
      }
    }
    if (changes["bgmTrack"] || changes["bgmTracks"]) {
      // Same hot-swap pattern for BGM. Whole rebuild is needed only when
      // the URL set actually changed; otherwise just patch existing
      // runtimes so the slider drag doesn't restart the music.
      const prevA = (changes["bgmTracks"]?.previousValue as BgmTrack[] | undefined) ?? [];
      const prevSingle = changes["bgmTrack"]?.previousValue as BgmTrack | null | undefined;
      const prevList: BgmTrack[] = prevA.length ? prevA : prevSingle?.url ? [prevSingle] : [];
      const nextList = this.effectiveBgmTracks;
      const sameSet =
        prevList.length === nextList.length &&
        this.bgmRuntimes.length === nextList.length &&
        prevList.every((p, i) => p.url === nextList[i]?.url);
      if (sameSet) {
        for (let i = 0; i < nextList.length; i++) {
          this.bgmRuntimes[i].track = nextList[i];
        }
        // Window / volume changes feed syncBgm immediately on the next tick.
        this.syncBgm();
      } else {
        this.rebuildBgmRuntimes();
        this.probeBgmDurations();
      }
    }
    if (changes["clipVolumes"]) {
      // Apply on every volume change, even when videoUrls also reported a
      // change in the same cycle. reset() doesn't drive syncVo() / volume
      // application by itself, so without this the slider only takes
      // effect on the next timeupdate (i.e. never while paused).
      this.applyDucking();
    }
    if (changes["clipTrims"] && !changes["videoUrls"]) {
      // Trims-only change: keep the slot URLs as-is, just recompute the
      // timeline math and resync the playhead to the (possibly new)
      // in-point of the active clip.
      this.recomputeTrimmedTimeline();
      this.seekActiveSlotToInPoint();
      this.currentTime = this.computeCurrentTime();
      if (this.totalDuration > 0) {
        this.progress = (this.currentTime / this.totalDuration) * 100;
      }
      this.videoTimeUpdate.emit(this.currentTime);
    }
  }

  constructor(
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngAfterViewInit(): void {
    queueMicrotask(() => {
      this.bindVoAudioElements();
      this.bindBgmAudioElements();
    });
    this.observeOverlayScale();
  }

  /**
   * Watch the video wrapper's width and recompute `overlayScale` so that
   * overlay sizes scale with the displayed video frame (e.g. fullscreen,
   * window resize). x/y stay percentage-based and need no scaling.
   */
  private observeOverlayScale(): void {
    const el = this.videoWrapperRef?.nativeElement;
    if (!el || typeof ResizeObserver === "undefined") {
      this.recomputeOverlayScale();
      return;
    }
    this.overlayResizeObserver = new ResizeObserver(() => {
      // zone.js does not reliably patch ResizeObserver, so recomputing here
      // can change the numbers with nothing re-rendering. Going into
      // fullscreen is where that shows: the picture box is recalculated,
      // the card keeps the size it had in the page, and a vertical card is
      // drawn at the width it had before.
      this.zone.run(() => {
        this.recomputeOverlayScale();
        this.cdr.markForCheck();
      });
    });
    this.overlayResizeObserver.observe(el);
    this.recomputeOverlayScale();
  }

  private recomputeOverlayScale(): void {
    const el = this.videoWrapperRef?.nativeElement;
    const box = el?.getBoundingClientRect();
    const w = el?.offsetWidth || box?.width || 0;
    const h = el?.offsetHeight || box?.height || 0;
    // Everything drawn is measured against the picture, not the window the
    // picture sits in. The two are the same until fullscreen.
    this.frameBox = this.containBox(w, h);
    // Spatial overlays and annotation pins still sit against the wrapper
    // and are still placed as a share of it, so their size keeps following
    // the wrapper too. Sizing them off the picture while positioning them
    // off the wrapper would be half a change. They have the same fullscreen
    // problem a card had, and the same remedy — moving them inside the
    // picture box — but that is the annotation feature, not this one.
    this.overlayScale =
      w > 0 ? w / MultiVideoPlayerComponent.OVERLAY_REFERENCE_WIDTH_PX : 1;
    // Text is sized as a share of the frame height, and slides move a share
    // of the frame, so keep both.
    this.overlayFrameHeightPx = this.frameBox.height;
    this.overlayFrameWidthPx = this.frameBox.width;
  }

  /**
   * The largest box of the cut's shape that fits inside `w` x `h`, centred
   * — the same box `object-fit: contain` paints the picture into.
   */
  private containBox(
    w: number,
    h: number,
  ): { width: number; height: number; left: number; top: number } {
    if (w <= 0 || h <= 0) return { width: 0, height: 0, left: 0, top: 0 };
    const aspect =
      this.frameAspect && this.frameAspect > 0
        ? this.frameAspect
        : this.playingAspect() ?? w / h;
    let width = w;
    let height = w / aspect;
    if (height > h) {
      height = h;
      width = h * aspect;
    }
    return { width, height, left: (w - width) / 2, top: (h - height) / 2 };
  }

  /** The shape of the video that is playing, if one is. */
  private playingAspect(): number | null {
    const el = this.activeEl;
    if (!el || !el.videoWidth || !el.videoHeight) return null;
    return el.videoWidth / el.videoHeight;
  }

  // ─── On-screen text (supers, spec §12) ───────────────────────────────
  @Input() textOverlays: TextOverlay[] = [];

  /**
   * The cut's shape as width / height — 1080/1920 for a vertical cut.
   *
   * The wrapper is not the picture. `object-fit: contain` letterboxes the
   * video inside it, and normally the wrapper is already the cut's shape so
   * the two agree. In fullscreen the wrapper becomes the whole screen, and
   * anything drawn at `inset: 0` — a title card above all, because it fills
   * its frame — is drawn across the screen instead of across the picture. A
   * vertical card came out landscape.
   *
   * Left null, the shape is taken from the video that is playing; a title
   * card has no video, which is exactly when it is needed, so the caller
   * should pass it.
   */
  @Input() frameAspect: number | null = null;

  /**
   * Where the picture actually sits inside the wrapper, in pixels. Drawn
   * content is positioned against this rather than against the wrapper.
   */
  frameBox: { width: number; height: number; left: number; top: number } = {
    width: 0,
    height: 0,
    left: 0,
    top: 0,
  };
  /** Size of the video surface: height turns `sizePct` into pixels, width
   *  sets how far a slide travels. */
  overlayFrameHeightPx = 0;
  overlayFrameWidthPx = 0;

  get visibleTextOverlays(): TextOverlay[] {
    const t = this.currentTime;
    return (this.textOverlays || []).filter(
      (o) => !!o.text && t >= o.startTime && t < o.endTime,
    );
  }

  /** Ease a 0..1 progress value the way the contract names it. */
  private ease(p: number, easing?: string): number {
    const t = Math.max(0, Math.min(1, p));
    switch (easing) {
      case "ease_in": return t * t;
      case "ease_out": return 1 - (1 - t) * (1 - t);
      case "ease_in_out": return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      default: return t; // linear
    }
  }

  /** How far through its in / out animation a piece of text is right now.
   *  1 = fully in place. Both return 1 when there is no animation. */
  private textProgress(o: TextOverlay, which: "in" | "out"): number {
    const spec = which === "in" ? o.animation?.in : o.animation?.out;
    const type = spec?.type ?? "none";
    if (!spec || type === "none") return 1;
    const dur = Math.max(0, spec.duration ?? 0);
    const delay = Math.max(0, spec.delay ?? 0);
    if (dur <= 0) return 1;
    const t = this.currentTime;
    const elapsed =
      which === "in" ? t - o.startTime - delay : o.endTime - delay - t;
    if (elapsed >= dur) return 1;
    if (elapsed <= 0) return 0;
    return this.ease(elapsed / dur, spec.easing);
  }

  /** Inline style for one piece of text: the fade and the slide, worked out
   *  from the playhead. The element is positioned by its centre, so the
   *  base translate stays in front. */
  textAnimStyle(o: TextOverlay): { opacity: number; transform: string } {
    const inP = this.textProgress(o, "in");
    const outP = this.textProgress(o, "out");
    const inType = o.animation?.in?.type ?? "none";
    const outType = o.animation?.out?.type ?? "none";
    const base = (o.opacityPct ?? 100) / 100;

    let opacity = base;
    if (inType === "fade") opacity = Math.min(opacity, base * inP);
    if (outType === "fade") opacity = Math.min(opacity, base * outP);

    // Slide distance: a share of the frame, so it looks the same at any size.
    const dx = (this.overlayFrameWidthPx || 0) * 0.18;
    const dy = (this.overlayFrameHeightPx || 0) * 0.14;
    let x = 0;
    let y = 0;
    // Coming in: start off-frame on the named side and travel to place.
    if (inP < 1) {
      const away = 1 - inP;
      if (inType === "slide_left") x -= dx * away;
      else if (inType === "slide_right") x += dx * away;
      else if (inType === "slide_up") y -= dy * away;
      else if (inType === "slide_down") y += dy * away;
    }
    // Going out: leave towards the named side.
    if (outP < 1) {
      const away = 1 - outP;
      if (outType === "slide_left") x -= dx * away;
      else if (outType === "slide_right") x += dx * away;
      else if (outType === "slide_up") y -= dy * away;
      else if (outType === "slide_down") y += dy * away;
    }
    const move = x || y ? ` translate(${x.toFixed(1)}px, ${y.toFixed(1)}px)` : "";
    const pull = (a: "start" | "center" | "end" | undefined): string =>
      a === "start" ? "0%" : a === "end" ? "-100%" : "-50%";
    const anchor = `translate(${pull(o.anchorX)}, ${pull(o.anchorY)})`;
    return { opacity, transform: `${anchor}${move}` };
  }

  /** The words to show now — the whole text, or the part a typewriter has
   *  reached. */
  textContent(o: TextOverlay): string {
    const spec = o.animation?.in;
    if ((spec?.type ?? "none") !== "typewriter") return o.text;
    const text = o.text ?? "";
    const delay = Math.max(0, spec?.delay ?? 0);
    const elapsed = this.currentTime - o.startTime - delay;
    if (elapsed <= 0) return "";
    const cps = spec?.typewriter?.charactersPerSecond;
    const perSec =
      cps && cps > 0
        ? cps
        : text.length / Math.max(0.1, spec?.duration ?? 1);
    return text.slice(0, Math.min(text.length, Math.floor(elapsed * perSec)));
  }

  /** Text height in pixels at the current player size. */
  textPx(o: TextOverlay): number {
    const h = this.overlayFrameHeightPx || 0;
    return Math.max(8, (h * (o.sizePct ?? 6)) / 100);
  }

  ngOnDestroy(): void {
    this.pauseAllVoAudio();
    this.pauseAllBgmAudio();
    this.stopAllSfx();
    if (this.audioCtx) {
      void this.audioCtx.close().catch(() => {});
      this.audioCtx = null;
    }
    this.sfxBuffers.clear();
    this.cancelAllFades();
    this.clearUnavailableSkip();
    if (this.overlayResizeEmitHandle !== null) {
      clearTimeout(this.overlayResizeEmitHandle);
      this.overlayResizeEmitHandle = null;
    }
    this.overlayResizeObserver?.disconnect();
    this.overlayResizeObserver = null;
    // Explicitly release both slot players so Chrome reclaims their
    // WebMediaPlayer slots immediately (rather than waiting for GC).
    for (const el of [
      this.videoARef?.nativeElement,
      this.videoBRef?.nativeElement,
    ]) {
      if (!el) continue;
      try {
        el.pause();
        el.removeAttribute("src");
        el.load();
      } catch {}
    }
    // Same for VO/BGM audio elements.
    for (const vo of this.voRuntimes) {
      const el = vo.el;
      if (!el) continue;
      try {
        el.removeAttribute("src");
        el.load();
      } catch {}
    }
    for (const bgm of this.bgmRuntimes) {
      const el = bgm.el;
      if (!el) continue;
      try {
        el.removeAttribute("src");
        el.load();
      } catch {}
    }
    // Invalidate any in-flight probes so they don't resolve into stale state.
    this.probeToken++;
    this.voProbeToken++;
    this.bgmProbeToken++;
  }

  private reset(): void {
    this.pauseAllVoAudio();
    this.stopAllSfx();
    this.clearBlend();
    this.currentIndex = 0;
    this.activeSlot = "A";
    this.slotAUrl = this.videoUrls?.[0] ?? null;
    this.slotBUrl = this.videoUrls?.[1] ?? null;
    this.isPlaying = false;
    this.currentTime = 0;
    this.progress = 0;
    this.clipRawDurations = [];
    this.clipDurations = [];
    this.cumulativeOffsets = [];
    this.totalDuration = 0;
    this.metadataReady = false;
    this.probedClipCount = 0;
    for (const vo of this.voRuntimes) vo.active = false;
  }

  private rebuildVoRuntimes(): void {
    this.pauseAllVoAudio();
    this.voRuntimes = (this.voTracks ?? []).map((track) => ({
      track,
      endTime:
        track.duration != null
          ? track.startTime + track.duration
          : Number.POSITIVE_INFINITY,
      active: false,
    }));
    queueMicrotask(() => this.bindVoAudioElements());
  }

  private bindVoAudioElements(): void {
    if (!this.voAudioRefs) return;
    const arr = this.voAudioRefs.toArray();
    this.voRuntimes.forEach((vo, i) => {
      vo.el = arr[i]?.nativeElement;
    });
  }

  private async probeVoDurations(): Promise<void> {
    const token = ++this.voProbeToken;
    const runtimes = this.voRuntimes;
    // Parallelize per-track probes (see probeDurations for rationale).
    await this.runWithConcurrency(runtimes.length, 6, async (i) => {
      const vo = runtimes[i];
      if (vo.track.duration != null && vo.track.duration > 0) return;
      try {
        const d = await this.getDuration(vo.track.url);
        if (token !== this.voProbeToken) return;
        // What's left of the file after the head-trim is what plays.
        const playable = Math.max(0, (Number.isFinite(d) ? d : 0) - Math.max(0, vo.track.sourceIn ?? 0));
        vo.endTime = vo.track.startTime + playable;
      } catch {
        if (token !== this.voProbeToken) return;
        vo.endTime = vo.track.startTime;
      }
    });
  }

  private pauseAllVoAudio(): void {
    for (const vo of this.voRuntimes) {
      if (!vo.el) continue;
      try {
        vo.el.pause();
        vo.el.currentTime = Math.max(0, vo.track.sourceIn ?? 0);
      } catch {}
      vo.active = false;
    }
  }

  /** Aggregate BGM tracks input: prefer the new array, fall back to the
   *  legacy single-track input. Filters out empty urls so the audio
   *  element list never renders a `<audio src="">`. */
  private get effectiveBgmTracks(): BgmTrack[] {
    if (this.bgmTracks && this.bgmTracks.length) {
      return this.bgmTracks.filter((t) => !!t?.url);
    }
    if (this.bgmTrack?.url) return [this.bgmTrack];
    return [];
  }

  private rebuildBgmRuntimes(): void {
    this.pauseAllBgmAudio();
    this.bgmRuntimes = this.effectiveBgmTracks.map((track) => ({
      track,
      duration: 0,
      active: false,
    }));
    queueMicrotask(() => this.bindBgmAudioElements());
  }

  private bindBgmAudioElements(): void {
    if (!this.bgmAudioRefs) return;
    const arr = this.bgmAudioRefs.toArray();
    this.bgmRuntimes.forEach((bgm, i) => {
      bgm.el = arr[i]?.nativeElement;
    });
  }

  private pauseAllBgmAudio(): void {
    for (const bgm of this.bgmRuntimes) {
      if (!bgm.el) continue;
      try {
        bgm.el.pause();
        bgm.el.currentTime = Math.max(0, bgm.track.sourceIn ?? 0);
      } catch {}
      bgm.active = false;
    }
  }

  /** Probe each runtime's source duration once. We need this so non-looping
   *  BGM blocks know when to stop, and looping blocks know when to wrap. */
  private async probeBgmDurations(): Promise<void> {
    const token = ++this.bgmProbeToken;
    const runtimes = this.bgmRuntimes;
    // Parallelize per-track probes (see probeDurations for rationale).
    await this.runWithConcurrency(runtimes.length, 6, async (i) => {
      const bgm = runtimes[i];
      try {
        const d = await this.getDuration(bgm.track.url);
        if (token !== this.bgmProbeToken) return;
        bgm.duration = Number.isFinite(d) ? d : 0;
      } catch {
        if (token !== this.bgmProbeToken) return;
        bgm.duration = 0;
      }
    });
  }

  /** Iterate every BGM runtime — for each, gate playback by its own
   *  render-timeline window (startTime → endTime), seek to the matching
   *  local offset, and apply ducking when any VO is active. */
  private syncBgm(): void {
    if (!this.bgmRuntimes.length) return;
    const global = this.currentTime;
    const videoPaused = this.activeEl?.paused ?? true;
    const anyVoActive = this.voRuntimes.some((v) => v.active);
    const mul = this.effectiveMaster();

    for (const bgm of this.bgmRuntimes) {
      if (!bgm.el) continue;
      const startT = bgm.track.startTime ?? 0;
      const endT = bgm.track.endTime;
      const looping = bgm.track.loop !== false;
      const inWindow =
        global >= startT && (endT == null || global < endT);
      // Head-trim in-point: the cue plays from here, and a loop repeats
      // this point onwards rather than the whole file.
      const inPoint = Math.max(0, bgm.track.sourceIn ?? 0);

      if (!inWindow) {
        if (!bgm.el.paused) {
          try {
            bgm.el.pause();
            bgm.el.currentTime = inPoint;
          } catch {}
        }
        bgm.active = false;
        continue;
      }

      const localTime = Math.max(0, global - startT);
      const playable = bgm.duration - inPoint;
      if (playable > 0) {
        const expected = looping
          ? inPoint + (localTime % playable)
          : Math.min(inPoint + localTime, bgm.duration);
        const drift = bgm.el.currentTime - expected;
        if (Math.abs(drift) > 0.5) {
          try {
            bgm.el.currentTime = expected;
          } catch {}
        }
      }

      // Native looping restarts at file time 0, which is wrong once a cue
      // is head-trimmed — the wrap is handled by the drift seek above.
      const nativeLoop = looping && inPoint <= 0;
      if (bgm.el.loop !== nativeLoop) bgm.el.loop = nativeLoop;
      if (bgm.el.playbackRate !== this.playbackRate) {
        bgm.el.playbackRate = this.playbackRate;
      }

      if (videoPaused && !bgm.el.paused) {
        bgm.el.pause();
      } else if (!videoPaused && bgm.el.paused) {
        bgm.el.play().catch(() => {});
      }
      bgm.active = true;

      const baseVol = bgm.track.volume ?? 0.3;
      // Ducking should LOWER the volume, never raise it — cap the duck
      // level by baseVol so that a muted track (volume = 0) stays silent
      // even while a VO is active. Previously a muted BGM would play back
      // at the duck level (~10%) whenever any VO ran, because targetVol
      // was set to duckedByVoTo without consulting baseVol.
      const duckVol = bgm.track.duckedByVoTo ?? 0.1;
      const targetVol = anyVoActive ? Math.min(baseVol, duckVol) : baseVol;
      this.fadeVolumeTo(bgm.el, targetVol * mul);
    }
  }

  private computeCurrentTime(): number {
    const el = this.activeEl;
    if (!el) return this.currentTime;
    const offset = this.cumulativeOffsets[this.currentIndex] ?? 0;
    const inT = this.getClipInTime(this.currentIndex);
    return offset + Math.max(0, el.currentTime - inT);
  }

  /** Fetch and decode one effect. Failures are swallowed: a sound that
   *  will not load should cost silence, not a broken player. */
  private async loadSfx(url: string): Promise<void> {
    if (!url || this.sfxBuffers.has(url) || this.sfxLoading.has(url)) return;
    const ctx = this.ensureAudioCtx();
    if (!ctx) return;
    this.sfxLoading.add(url);
    try {
      const res = await fetch(url);
      const bytes = await res.arrayBuffer();
      this.sfxBuffers.set(url, await ctx.decodeAudioData(bytes));
    } catch {
      // Left out of the cache so a later attempt can retry.
    } finally {
      this.sfxLoading.delete(url);
    }
  }

  private ensureAudioCtx(): AudioContext | null {
    if (this.audioCtx) return this.audioCtx;
    try {
      const Ctor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      this.audioCtx = Ctor ? new Ctor() : null;
    } catch {
      this.audioCtx = null;
    }
    return this.audioCtx;
  }

  /** Stop everything sounding. Called on pause, seek and teardown — an
   *  effect must never outlive the playhead that started it. */
  private stopAllSfx(): void {
    for (const src of this.sfxLive.values()) {
      try {
        src.stop();
      } catch {}
    }
    this.sfxLive.clear();
    this.sfxFired.clear();
  }

  /** How far ahead of a sound we are willing to schedule it, in seconds.
   *
   *  Waiting until the playhead is inside the sound means starting it
   *  late by however long the last frame took, and means missing it
   *  outright when a frame runs longer than the sound itself — a real
   *  risk for a 0.3s effect on a busy page, and one that was seen. Web
   *  Audio can be told to start at a future moment to the sample, so the
   *  sound is handed over early with an exact time and the frame jitter
   *  stops mattering. */
  private static readonly SFX_LOOKAHEAD_SEC = 0.15;

  /** Start one effect. `ahead` is how long until it should sound (0 when
   *  the playhead is already inside it, e.g. after a seek into the
   *  middle), and `into` how far past its start we already are. */
  private fireSfx(t: SfxTrack, into: number, ahead = 0): void {
    const ctx = this.audioCtx;
    const buf = this.sfxBuffers.get(t.url);
    if (!ctx || !buf) return;
    this.sfxFired.add(t.id);
    try {
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const gain = ctx.createGain();
      gain.gain.value = Math.max(0, Math.min(1, t.volume ?? 1));
      src.connect(gain).connect(ctx.destination);
      const offset = Math.max(0, (t.sourceIn ?? 0) + into);
      const remaining = Math.max(0, t.duration - into);
      if (remaining <= 0) return;
      const at = ctx.currentTime + Math.max(0, ahead);
      if (t.loop) {
        // Repeat the kept part of the file, not the whole of it: a
        // head-trimmed effect should loop from where it now starts,
        // otherwise the repeat plays audio the editor cut off.
        src.loop = true;
        src.loopStart = Math.max(0, t.sourceIn ?? 0);
        src.loopEnd = buf.duration;
        // `duration` bounds a looping source in current browsers, but the
        // spec leaves it ambiguous enough that a stray sound looping for
        // ever is a real risk. The explicit stop costs nothing.
        src.start(at, offset);
        src.stop(at + remaining);
      } else {
        // An exact future moment, not "now" — this is the whole reason
        // the lane does not use an <audio> element.
        src.start(at, offset, remaining);
      }
      this.sfxLive.set(t.id, src);
      src.onended = () => this.sfxLive.delete(t.id);
    } catch {}
  }

  private syncSfx(): void {
    if (!this.sfxTracks.length) {
      if (this.sfxLive.size) this.stopAllSfx();
      return;
    }
    // A title card has no <video> to be unpaused, so asking the element
    // whether it is playing says "no" for the whole card and every effect
    // over one stayed silent. Cards and unreachable clips run on the rAF
    // clock instead, where `isPlaying` is the truth — the same test the
    // progress bar makes at `isCurrentSlotTimerDriven`.
    const playing = this.isCurrentSlotTimerDriven
      ? this.isPlaying
      : !(this.activeEl?.paused ?? true);
    if (!playing) {
      if (this.sfxLive.size || this.sfxFired.size) this.stopAllSfx();
      return;
    }
    const ctx = this.ensureAudioCtx();
    if (!ctx) return;
    // Browsers start the context suspended until a gesture; playing IS one.
    if (ctx.state === "suspended") void ctx.resume().catch(() => {});

    const global = this.currentTime;
    const look = MultiVideoPlayerComponent.SFX_LOOKAHEAD_SEC;
    for (const t of this.sfxTracks) {
      const end = t.startTime + t.duration;
      // Due soon, or already under way (a seek can land mid-sound).
      const due = global >= t.startTime - look && global < end;
      if (!due) {
        // Leaving the window re-arms it, so seeking back replays the sound.
        this.sfxFired.delete(t.id);
        continue;
      }
      if (this.sfxFired.has(t.id)) continue;
      if (!this.sfxBuffers.has(t.url)) {
        void this.loadSfx(t.url);
        continue;
      }
      const ahead = Math.max(0, t.startTime - global);
      const into = Math.max(0, global - t.startTime);
      this.fireSfx(t, into, ahead);
    }
  }

  private syncVo(): void {
    this.syncBgm();
    this.syncSfx();
    if (!this.voRuntimes.length) return;
    const global = this.currentTime;
    const videoPaused = this.activeEl?.paused ?? true;

    for (const vo of this.voRuntimes) {
      if (!vo.el) continue;
      const inWindow =
        global >= vo.track.startTime && global < vo.endTime;

      // Head-trim: playback starts `sourceIn` seconds into the file, so
      // every local time below is measured from there, not from 0.
      const inPoint = Math.max(0, vo.track.sourceIn ?? 0);

      if (!inWindow) {
        if (vo.active) {
          try {
            vo.el.pause();
            vo.el.currentTime = inPoint;
          } catch {}
          vo.active = false;
        }
        continue;
      }

      const expectedLocal = inPoint + (global - vo.track.startTime);

      if (!vo.active) {
        try {
          vo.el.currentTime = expectedLocal;
          vo.el.volume = (vo.track.volume ?? 1) * this.effectiveMaster();
          vo.el.playbackRate = this.playbackRate;
        } catch {}
        if (!videoPaused) vo.el.play().catch(() => {});
        vo.active = true;
        continue;
      }

      if (videoPaused && !vo.el.paused) {
        vo.el.pause();
      } else if (!videoPaused && vo.el.paused) {
        vo.el.play().catch(() => {});
      }

      const drift = vo.el.currentTime - expectedLocal;
      if (Math.abs(drift) > this.DRIFT_HARD_RESYNC_SEC) {
        try {
          vo.el.currentTime = expectedLocal;
        } catch {}
      }

      // Re-apply volume on every tick so slider drags take effect
      // immediately — the original implementation only wrote vo.el.volume
      // on the inactive→active transition, so once a VO was playing the
      // user's volume edits never reached the audio element until the
      // next scrub / play-pause cycle.
      const targetVol = (vo.track.volume ?? 1) * this.effectiveMaster();
      if (Math.abs(vo.el.volume - targetVol) > 0.001) {
        try {
          vo.el.volume = Math.max(0, Math.min(1, targetVol));
        } catch {}
      }
    }

    this.applyDucking();
  }

  private applyDucking(): void {
    const base = this.duckedBaseVolume();
    const a = this.videoARef?.nativeElement;
    const b = this.videoBRef?.nativeElement;
    if (a) this.fadeVolumeTo(a, base * this.getSlotClipVolume("A"));
    if (b) this.fadeVolumeTo(b, base * this.getSlotClipVolume("B"));
  }

  /** Master volume with any voiceover ducking applied — the level a slot
   *  would play at before its own per-clip volume. */
  private duckedBaseVolume(): number {
    const explicit = this.voRuntimes.filter(
      (v) => v.active && v.track.duckVideoTo != null,
    );
    let targetVol = 1;
    if (explicit.length > 0) {
      targetVol = Math.min(...explicit.map((v) => v.track.duckVideoTo ?? 1));
    } else if (
      this.defaultDuckTo != null &&
      this.voRuntimes.some((v) => v.active)
    ) {
      targetVol = Math.max(0, Math.min(1, this.defaultDuckTo));
    }
    return targetVol * this.effectiveMaster();
  }

  /** What this slot should be playing at with everything but a fade dip. */
  private slotBaseVolume(slot: Slot): number {
    return this.duckedBaseVolume() * this.getSlotClipVolume(slot);
  }

  /**
   * Effective per-clip volume override for a given slot's currently loaded
   * clip. Returns 1 when no override has been provided for that index, so
   * by default nothing changes.
   */
  private getSlotClipVolume(slot: Slot): number {
    // Unavailable clips hard-clamp to 0 regardless of any caller-provided
    // volume so the user can't hear a clip the backend has marked off-limits.
    // The visual overlay is added separately in the template; this just
    // covers the audio side. Mirrors the "muted, no unmute" UX the user
    // asked for: master mute or per-clip volume can't override 0 here.
    const idx = slot === this.activeSlot ? this.currentIndex : this.currentIndex + 1;
    if (!this.isClipAvailable(idx)) return 0;
    if (!this.clipVolumes?.length) return 1;
    const raw = this.clipVolumes[idx];
    if (raw == null || !Number.isFinite(raw)) return 1;
    return Math.max(0, Math.min(1, raw));
  }

  /** Per-index availability lookup. Defaults to true when the array is
   *  empty or the entry is missing — preserves legacy callers. */
  private isClipAvailable(idx: number): boolean {
    if (!this.clipAvailability?.length) return true;
    const entry = this.clipAvailability[idx];
    return !entry || entry.available !== false;
  }

  /** Template-facing: is the CURRENT clip unavailable? Drives the overlay.
   *  A title card is not "unavailable" — it has no file by design, and the
   *  card itself is drawn instead. */
  get isCurrentClipUnavailable(): boolean {
    return !this.isClipAvailable(this.currentIndex) && !this.currentCard;
  }

  /** No <video> can carry this slot: either its file is missing or the slot
   *  is a drawn title card. Both need the timer to move the playhead —
   *  which is why this is separate from the overlay's test above. */
  private get isCurrentSlotTimerDriven(): boolean {
    return !this.isClipAvailable(this.currentIndex) || !!this.currentCard;
  }

  /** The card filling the frame right now, if this slot is one. */
  get currentCard(): CardSlide | null {
    return this.cardSlides?.[this.currentIndex] ?? null;
  }

  /** How a background picture fills the card, in CSS terms
   *  (the contract's `stretch` is CSS's `fill`). */
  cardObjectFit(c: CardSlide): string {
    return c.backgroundFit === "stretch" ? "fill" : c.backgroundFit || "cover";
  }

  /** Logo width in pixels at the current player size. */
  cardLogoPx(c: CardSlide): number {
    return Math.max(8, (this.overlayFrameWidthPx * (c.logo?.widthPct ?? 14)) / 100);
  }

  /** Title size in pixels at the current player size. */
  cardTitlePx(c: CardSlide): number {
    return Math.max(10, (this.overlayFrameHeightPx * (c.titleSizePct ?? 9)) / 100);
  }

  // ─── Fades and transitions (spec §10) ────────────────────────────────
  // A fade dips this clip's own start or end; a transition dips the join,
  // half out of one clip and half into the next. Nothing about the timing
  // changes (§10.3) — only what the picture and this clip's sound do.

  /** True for the two transitions that only darken the picture — the ones
   *  a single `<video>` can show honestly. */
  private isDipKind(kind: TransitionKind): boolean {
    return kind === "dip_to_black" || kind === "dip_to_white";
  }

  /** A join dips when it is a dip, or when it wants to blend but one side
   *  has no picture to blend with (a title card, a missing file). */
  private dipFor(t: JoinTransition | null | undefined): string | null {
    if (!t || !(t.duration > 0)) return null;
    if (t.kind === "dip_to_white") return "#FFFFFF";
    if (t.kind === "dip_to_black") return "#000000";
    return t.blendable === false ? "#000000" : null;
  }

  /** The dip over the picture right now, or null when there is none. */
  get pictureDip(): { color: string; opacity: number } | null {
    const { start, length } = this.currentSlotWindow();
    if (!(length > 0)) return null;
    const t = this.currentTime - start;
    if (t < -0.01 || t > length + 0.01) return null;
    let opacity = 0;
    let color = "#000000";

    const fade = this.clipFades?.[this.currentIndex];
    if (fade) {
      if (fade.in > 0 && t < fade.in) {
        opacity = Math.max(opacity, 1 - t / fade.in);
      }
      if (fade.out > 0 && length - t < fade.out) {
        opacity = Math.max(opacity, 1 - (length - t) / fade.out);
      }
    }
    // Going out of this clip into the next.
    const after = this.transitions?.[this.currentIndex];
    const afterDip = this.dipFor(after);
    if (after && afterDip) {
      const half = after.duration / 2;
      const remaining = length - t;
      if (remaining < half) {
        const p = 1 - remaining / half;
        if (p > opacity) {
          opacity = p;
          color = afterDip;
        }
      }
    }
    // Coming into this clip from the one before.
    const beforeJoin = this.transitions?.[this.currentIndex - 1];
    const beforeDip = this.dipFor(beforeJoin);
    if (beforeJoin && beforeDip) {
      const half = beforeJoin.duration / 2;
      if (t < half) {
        const p = 1 - t / half;
        if (p > opacity) {
          opacity = p;
          color = beforeDip;
        }
      }
    }
    return opacity > 0.002
      ? { color, opacity: Math.min(1, opacity) }
      : null;
  }

  /** True while any dip is on screen — used to decide whether the sound
   *  needs following it down. */
  private dipEngaged = false;

  // ─── Blended joins: crossfade, slide, wipe ───────────────────────────
  // These need the outgoing and incoming pictures on screen together. The
  // incoming clip lives in the spare slot already (it is preloaded), so
  // during the last D seconds of the outgoing clip we play it there from
  // `in − D`, which puts it exactly on its own in-point at the cut. That
  // is what keeps §10.3: the join borrows the incoming clip's spare
  // footage, so no boundary moves and the cut keeps its length. Where
  // there is no spare, the incoming clip holds its first frame until it
  // has some — the blend still lasts the same time.

  /** The join being blended through right now, and how far into it. */
  private currentBlend(): { kind: TransitionKind; duration: number; p: number } | null {
    const t = this.transitions?.[this.currentIndex];
    if (!t || !(t.duration > 0)) return null;
    if (this.isDipKind(t.kind) || t.blendable === false) return null;
    if (this.currentIndex >= this.videoUrls.length - 1) return null;
    // Only a real, playable slot can be blended into or out of.
    if (this.currentCard || this.cardSlides?.[this.currentIndex + 1]) return null;
    if (!this.isClipAvailable(this.currentIndex) || !this.isClipAvailable(this.currentIndex + 1)) {
      return null;
    }
    const { start, length } = this.currentSlotWindow();
    if (!(length > 0)) return null;
    const remaining = length - (this.currentTime - start);
    if (remaining < -0.02 || remaining > t.duration) return null;
    const p = Math.max(0, Math.min(1, 1 - remaining / t.duration));
    return { kind: t.kind, duration: t.duration, p };
  }

  private get inactiveEl(): HTMLVideoElement | null {
    return this.activeSlot === "A"
      ? (this.videoBRef?.nativeElement ?? null)
      : (this.videoARef?.nativeElement ?? null);
  }

  /** True while the spare slot is showing, so we know to tidy up after. */
  private blendEngaged = false;
  /** Where the incoming clip was put last tick — used on the switch to
   *  decide whether it still needs seeking to its in-point. */
  private blendPrerollTime: number | null = null;

  /** Drive the incoming clip and paint the blend. */
  private tickBlend(): void {
    const blend = this.currentBlend();
    const incoming = this.inactiveEl;
    const outgoing = this.activeEl;
    if (!blend || !incoming || !outgoing) {
      if (this.blendEngaged) this.clearBlend();
      return;
    }
    // Where the incoming clip should be in its own footage. It reaches its
    // in-point exactly as the cut arrives; before that it plays its spare,
    // and holds frame 0 if it hasn't any.
    const inPoint = this.getClipInTime(this.currentIndex + 1);
    const want = Math.max(0, inPoint - (1 - blend.p) * blend.duration);
    if (Math.abs(incoming.currentTime - want) > 0.12) {
      try {
        incoming.currentTime = want;
      } catch {}
    }
    this.blendPrerollTime = want;
    if (this.isPlaying && incoming.paused && want > 0) {
      this.playEl(incoming);
    } else if (!this.isPlaying && !incoming.paused) {
      incoming.pause();
    }
    this.paintBlend(blend, incoming, outgoing);
    // Both clips' own sound blends with the picture (§10.3).
    const outVol = this.slotBaseVolume(this.activeSlot) * (1 - blend.p);
    const inVol =
      this.duckedBaseVolume() * this.getSlotClipVolume(this.activeSlot === "A" ? "B" : "A") * blend.p;
    this.setVolumeDirect(outgoing, outVol);
    this.setVolumeDirect(incoming, inVol);
    this.blendEngaged = true;
  }

  /** Put the incoming clip over the outgoing one the way this kind of
   *  join looks: a blend, a push from a side, or a reveal from an edge.
   *  The incoming element is lifted above the outgoing for the duration
   *  and both are left exactly as they were afterwards. */
  private paintBlend(
    blend: { kind: TransitionKind; p: number },
    incoming: HTMLVideoElement,
    outgoing: HTMLVideoElement,
  ): void {
    const p = blend.p;
    const pct = p * 100;
    incoming.style.zIndex = "2";
    outgoing.style.zIndex = "1";
    incoming.style.opacity = "1";
    incoming.style.transform = "";
    incoming.style.clipPath = "";
    outgoing.style.transform = "";
    switch (blend.kind) {
      case "crossfade":
        incoming.style.opacity = String(p);
        break;
      // The word names the side the new picture comes FROM, and the old
      // one is pushed out of the opposite side (contract §12b).
      case "slide_left":
        incoming.style.transform = `translateX(${-100 + pct}%)`;
        outgoing.style.transform = `translateX(${pct}%)`;
        break;
      case "slide_right":
        incoming.style.transform = `translateX(${100 - pct}%)`;
        outgoing.style.transform = `translateX(${-pct}%)`;
        break;
      case "slide_up":
        incoming.style.transform = `translateY(${-100 + pct}%)`;
        outgoing.style.transform = `translateY(${pct}%)`;
        break;
      case "slide_down":
        incoming.style.transform = `translateY(${100 - pct}%)`;
        outgoing.style.transform = `translateY(${-pct}%)`;
        break;
      // A wipe uncovers the new picture from the named edge; the old one
      // stays put underneath.
      case "wipe_left":
        incoming.style.clipPath = `inset(0 ${100 - pct}% 0 0)`;
        break;
      case "wipe_right":
        incoming.style.clipPath = `inset(0 0 0 ${100 - pct}%)`;
        break;
      case "wipe_up":
        incoming.style.clipPath = `inset(0 0 ${100 - pct}% 0)`;
        break;
      case "wipe_down":
        incoming.style.clipPath = `inset(${100 - pct}% 0 0 0)`;
        break;
    }
  }

  /** Start a slot playing, and mean it.
   *
   *  `HTMLMediaElement.play()` returns a promise the browser rejects when a
   *  new source load interrupts it — which is exactly what happens when the
   *  user scrubs back to an earlier clip (the slot's `src` is reassigned)
   *  and presses play in the same breath. Every call site swallowed that
   *  rejection, so the player sat paused while its own `isPlaying` said
   *  otherwise: the button showed ⏸ and nothing moved. Try again as soon as
   *  the element has frames, unless the user has since paused. */
  private playEl(el: HTMLMediaElement): void {
    el.play().catch(() => {
      const retry = () => {
        el.removeEventListener("canplay", retry);
        if (this.isPlaying && el.paused) el.play().catch(() => {});
      };
      el.addEventListener("canplay", retry);
      // `canplay` has often already fired by the time the rejection lands.
      setTimeout(retry, 120);
    });
  }

  /** Set a volume now, dropping any duck ramp that is mid-flight. */
  private setVolumeDirect(el: HTMLMediaElement, v: number): void {
    const state = this.fadeStateByEl.get(el);
    if (state?.handle != null) cancelAnimationFrame(state.handle);
    this.fadeStateByEl.delete(el);
    el.volume = Math.max(0, Math.min(1, v));
  }

  /** Take the spare slot back out of sight and hand the volumes back to
   *  the normal path. Safe to call at any time. */
  private clearBlend(): void {
    for (const el of [this.videoARef?.nativeElement, this.videoBRef?.nativeElement]) {
      if (!el) continue;
      el.style.zIndex = "";
      el.style.opacity = "";
      el.style.transform = "";
      el.style.clipPath = "";
    }
    const spare = this.inactiveEl;
    if (spare && !spare.paused) {
      try {
        spare.pause();
      } catch {}
    }
    this.blendEngaged = false;
    this.blendPrerollTime = null;
    this.applyDucking();
  }

  /** Paint the dip and pull this clip's sound down with it (§10.3).
   *  Driven from the frame loops rather than change detection: Angular
   *  only ticks a few times a second here, which would make a
   *  half-second fade visibly stepped. */
  private tickDip(): void {
    const dip = this.pictureDip;
    const box = this.videoDimRef?.nativeElement;
    if (box) {
      box.style.background = dip ? dip.color : "";
      box.style.opacity = dip ? String(dip.opacity) : "0";
    }
    this.tickBlend();
    // A blended join owns both slots' volumes while it runs.
    if (!this.blendEngaged) this.applyDipToSound(dip);
  }

  /** The clip's own sound follows its picture (§10.3): under a dip it is
   *  pulled down by the same amount, and handed back to the normal
   *  volume/ducking path on the way out. VO, music and effects are not
   *  touched — they have their own fades. */
  private applyDipToSound(dip: { color: string; opacity: number } | null): void {
    const el = this.activeEl;
    if (!el) return;
    if (dip) {
      // Drop any in-flight duck ramp so the two don't fight over volume.
      const state = this.fadeStateByEl.get(el);
      if (state?.handle != null) cancelAnimationFrame(state.handle);
      this.fadeStateByEl.delete(el);
      const target = this.slotBaseVolume(this.activeSlot) * (1 - dip.opacity);
      el.volume = Math.max(0, Math.min(1, target));
      this.dipEngaged = true;
    } else if (this.dipEngaged) {
      this.dipEngaged = false;
      this.applyDucking();
    }
  }

  /** Where the playhead sits inside the current slot, in seconds from its
   *  start, and how long the slot runs. */
  private currentSlotWindow(): { start: number; length: number } {
    const start = this.cumulativeOffsets[this.currentIndex] ?? 0;
    const length = Math.max(
      0,
      this.getClipOutTime(this.currentIndex) - this.getClipInTime(this.currentIndex),
    );
    return { start, length };
  }

  /** The card's own fade / slide / zoom, worked out from the playhead. */
  cardAnimStyle(c: CardSlide): { opacity: number; transform: string } {
    const { start, length } = this.currentSlotWindow();
    const elapsed = this.currentTime - start;
    const inSpec = c.animation?.in;
    const outSpec = c.animation?.out;
    const inType = inSpec?.type ?? "none";
    const outType = outSpec?.type ?? "none";
    const inDur = Math.max(0, inSpec?.duration ?? 0);
    const outDur = Math.max(0, outSpec?.duration ?? 0);
    const inP = inType === "none" || inDur <= 0 ? 1 : this.ease(Math.min(1, Math.max(0, elapsed / inDur)), inSpec?.easing);
    const remaining = length - elapsed;
    const outP = outType === "none" || outDur <= 0 ? 1 : this.ease(Math.min(1, Math.max(0, remaining / outDur)), outSpec?.easing);

    let opacity = 1;
    if (inType === "fade") opacity = Math.min(opacity, inP);
    if (outType === "fade") opacity = Math.min(opacity, outP);

    const dx = (this.overlayFrameWidthPx || 0) * 0.25;
    const dy = (this.overlayFrameHeightPx || 0) * 0.2;
    let x = 0;
    let y = 0;
    let scale = 1;
    if (inP < 1) {
      const away = 1 - inP;
      if (inType === "slide_left") x -= dx * away;
      else if (inType === "slide_right") x += dx * away;
      else if (inType === "slide_up") y -= dy * away;
      else if (inType === "slide_down") y += dy * away;
      else if (inType === "zoom_in") scale *= 0.85 + 0.15 * inP;
    }
    if (outP < 1) {
      const away = 1 - outP;
      if (outType === "slide_left") x -= dx * away;
      else if (outType === "slide_right") x += dx * away;
      else if (outType === "slide_up") y -= dy * away;
      else if (outType === "slide_down") y += dy * away;
      else if (outType === "zoom_out") scale *= 0.85 + 0.15 * outP;
    }
    const parts: string[] = [];
    if (x || y) parts.push(`translate(${x.toFixed(1)}px, ${y.toFixed(1)}px)`);
    if (scale !== 1) parts.push(`scale(${scale.toFixed(3)})`);
    return { opacity, transform: parts.join(" ") || "none" };
  }

  /** Single unified message for the overlay — keeps internal backend status
   *  ("unapproved", "deleted", "processing", etc.) out of the user-facing
   *  copy. The machine-readable reason is still preserved on the V-track
   *  block chip for editors who need to know WHY. */
  get currentClipUnavailableReason(): string {
    return "Clip is no longer available to view";
  }

  /** Template-facing: is the CURRENT clip mid-AI-Edit? Drives the
   *  "Editing…" overlay on the player surface. */
  get isCurrentClipEditing(): boolean {
    const entry = this.clipEditStates?.[this.currentIndex];
    return !!entry?.isEditing;
  }
  /** Template-facing: did the CURRENT clip's last AI Edit attempt fail?
   *  Drives the red error overlay. */
  get isCurrentClipEditError(): boolean {
    const entry = this.clipEditStates?.[this.currentIndex];
    return !!entry?.isEditError;
  }
  /** Backend-supplied error message for the current clip (when
   *  isCurrentClipEditError is true). Fallback copy is generic. */
  get currentClipEditErrorMessage(): string {
    const entry = this.clipEditStates?.[this.currentIndex];
    return entry?.editErrorMessage || "AI edit failed — try again";
  }

  private fadeVolumeTo(
    el: HTMLMediaElement,
    target: number,
    durationMs: number = this.duckFadeMs,
  ): void {
    const clamped = Math.max(0, Math.min(1, target));
    const existing = this.fadeStateByEl.get(el);
    if (existing && existing.target === clamped) return;
    if (existing?.handle != null) cancelAnimationFrame(existing.handle);

    const start = el.volume;
    if (Math.abs(start - clamped) < 0.001) {
      el.volume = clamped;
      this.fadeStateByEl.set(el, { target: clamped, handle: null });
      return;
    }

    const startTime = performance.now();
    const state: { target: number; handle: number | null } = {
      target: clamped,
      handle: null,
    };
    this.fadeStateByEl.set(el, state);

    const tick = () => {
      const elapsed = performance.now() - startTime;
      const t = Math.min(1, elapsed / Math.max(1, durationMs));
      const eased = 1 - Math.pow(1 - t, 2);
      el.volume = start + (clamped - start) * eased;
      if (t < 1) {
        state.handle = requestAnimationFrame(tick);
      } else {
        el.volume = clamped;
        state.handle = null;
      }
    };
    state.handle = requestAnimationFrame(tick);
  }

  private cancelAllFades(): void {
    for (const state of this.fadeStateByEl.values()) {
      if (state.handle != null) cancelAnimationFrame(state.handle);
    }
    this.fadeStateByEl.clear();
  }

  private scheduleRvfc(el: HTMLVideoElement | null): void {
    if (!el) return;
    const anyEl = el as any;
    if (typeof anyEl.requestVideoFrameCallback !== "function") return;
    const tick = () => {
      if (el !== this.activeEl) return;
      if (el.paused) return;
      this.currentTime = this.computeCurrentTime();
      if (this.totalDuration > 0) {
        this.progress = (this.currentTime / this.totalDuration) * 100;
      }
      this.syncVo();
      this.tickDip();
      this.videoTimeUpdate.emit(this.currentTime);
      (el as any).requestVideoFrameCallback(tick);
    };
    anyEl.requestVideoFrameCallback(tick);
  }

  private get activeEl(): HTMLVideoElement | null {
    return this.activeSlot === "A"
      ? (this.videoARef?.nativeElement ?? null)
      : (this.videoBRef?.nativeElement ?? null);
  }

  private async probeDurations(): Promise<void> {
    const token = ++this.probeToken;
    const urls = [...this.videoUrls];
    const durations: number[] = new Array(urls.length).fill(0);
    this.probedClipCount = 0;

    // Probe each clip's metadata in parallel with a concurrency cap of 6.
    // The old sequential loop turned a 23-clip render into ~5-10s of
    // "Preparing render…" over a real CDN; running in parallel brings that
    // down to roughly the slowest probe. Cap keeps us inside the browser's
    // per-host connection limit + Chrome's WebMediaPlayer pool.
    const probeOne = async (i: number): Promise<void> => {
      if (token !== this.probeToken) return;
      const url = urls[i];
      // Skip the probe entirely for clips the caller has marked unavailable
      // — their signed URLs often 404, and the fallback duration is what
      // the timeline should use anyway.
      if (!this.isClipAvailable(i)) {
        durations[i] = this.clipFallbackDurations[i] || 0;
      } else {
        try {
          const d = await this.getDuration(url);
          durations[i] =
            Number.isFinite(d) && d > 0 ? d : (this.clipFallbackDurations[i] || 0);
        } catch {
          durations[i] = this.clipFallbackDurations[i] || 0;
        }
      }
      if (token !== this.probeToken) return;
      // Progress counter — bumps per resolved probe regardless of order so
      // the overlay's "Loading clip X of Y" still ticks upward.
      this.probedClipCount++;
    };
    await this.runWithConcurrency(urls.length, 6, probeOne);
    if (token !== this.probeToken) return;

    this.clipRawDurations = durations;
    this.recomputeTrimmedTimeline();
    this.metadataReady = true;
    // After the very first probe, nudge the active slot to its in-point —
    // we couldn't do that earlier because the video element wasn't loaded.
    this.seekActiveSlotToInPoint();
    // If the initial clip is unavailable, kick off the auto-skip timer so
    // playback can move past it without depending on the <video> element.
    this.scheduleUnavailableSkipIfNeeded();
  }

  /** Run `count` tasks (each identified by its index) with at most
   *  `concurrency` in flight at a time. Resolves when every task settles;
   *  never rejects (individual tasks are expected to swallow their own
   *  errors so the pool doesn't stall). */
  private async runWithConcurrency(
    count: number,
    concurrency: number,
    task: (index: number) => Promise<void>,
  ): Promise<void> {
    if (count <= 0) return;
    let next = 0;
    const workers = new Array(Math.min(concurrency, count))
      .fill(0)
      .map(async () => {
        while (true) {
          const i = next++;
          if (i >= count) return;
          try {
            await task(i);
          } catch {}
        }
      });
    await Promise.all(workers);
  }

  /**
   * Source-clip in-point for a clip index. Clamped to [0, rawDuration] so
   * a stale trim from a previous shot list can't seek into negative or
   * past-the-end territory.
   */
  private getClipInTime(idx: number): number {
    const raw = this.clipRawDurations[idx] ?? 0;
    const t = this.clipTrims?.[idx];
    if (!t || !Number.isFinite(t.in)) return 0;
    return Math.max(0, Math.min(raw, t.in));
  }

  /** Source-clip out-point — defaults to the raw duration if no trim. */
  private getClipOutTime(idx: number): number {
    const raw = this.clipRawDurations[idx] ?? 0;
    const t = this.clipTrims?.[idx];
    if (!t || !Number.isFinite(t.out)) return raw;
    return Math.max(this.getClipInTime(idx), Math.min(raw, t.out));
  }

  /**
   * Recompute the trimmed-duration array + cumulative offsets + total
   * duration from `clipRawDurations` and the current `clipTrims`. Called
   * after a probe pass and whenever `clipTrims` changes on its own.
   */
  private recomputeTrimmedTimeline(): void {
    const n = this.clipRawDurations.length;
    const effective: number[] = new Array(n).fill(0);
    for (let i = 0; i < n; i++) {
      effective[i] = Math.max(0, this.getClipOutTime(i) - this.getClipInTime(i));
    }
    this.clipDurations = effective;
    let acc = 0;
    this.cumulativeOffsets = effective.map((d) => {
      const v = acc;
      acc += d;
      return v;
    });
    this.totalDuration = acc;
  }

  /**
   * Push the currently-active <video> element's playhead into its clip's
   * trim window. Only adjusts when the current frame is OUTSIDE the new
   * [in, out] window — otherwise we'd yank playback every mousemove
   * while the user is dragging the right handle of the clip they're
   * watching. Used after first metadata probe and whenever clipTrims
   * changes.
   */
  private seekActiveSlotToInPoint(): void {
    const el = this.activeEl;
    if (!el) return;
    const inT = this.getClipInTime(this.currentIndex);
    const outT = this.getClipOutTime(this.currentIndex);
    const current = Number.isFinite(el.currentTime) ? el.currentTime : 0;
    const inWindow = current >= inT && current < outT;
    // Already inside the trim window — don't disturb the user's playback.
    if (inWindow && el.readyState >= 1) return;
    // Clamp to in if before, or to just-before-out if past the out point.
    const target = current < inT ? inT : Math.max(inT, outT - 0.01);
    const apply = () => {
      try { el.currentTime = target; } catch {}
    };
    if (el.readyState >= 1) {
      apply();
    } else {
      const onMeta = () => {
        apply();
        el.removeEventListener("loadedmetadata", onMeta);
      };
      el.addEventListener("loadedmetadata", onMeta);
    }
  }

  private getDuration(url: string): Promise<number> {
    return new Promise((resolve, reject) => {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.muted = true;
      // Detach the WebMediaPlayer once we have what we need. Chrome caps
      // these at ~75 per page; failing to release them after probing leaks
      // a player per clip and eventually triggers the
      // "Blocked attempt to create a WebMediaPlayer" intervention.
      const release = () => {
        try {
          v.removeAttribute("src");
          v.load();
        } catch {}
      };
      v.onloadedmetadata = () => {
        const d = v.duration;
        release();
        resolve(d);
      };
      v.onerror = () => {
        release();
        reject(new Error("metadata load failed: " + url));
      };
      v.src = url;
    });
  }

  togglePlay(): void {
    const el = this.activeEl;
    if (!el) return;
    // A drawn slot (title card) or a missing file has no <video> to ask:
    // its element is always `paused`, so without this the button would
    // read "play" for ever and never stop the timer carrying the playhead.
    const running = this.isCurrentSlotTimerDriven ? this.isPlaying : !el.paused;
    if (!running) {
      // Replay-from-start guard. After onEnded fires on the last clip the
      // active <video> sits at its source end-frame and currentTime equals
      // totalDuration. Calling play() in that state is a no-op (or it
      // instantly re-fires "ended"), so the user thinks the play button is
      // broken. Detect end-of-stream here and route through replayFromStart()
      // which seeks back to global 0 + flips slots to clip[0] before playing.
      const atEnd =
        this.totalDuration > 0 &&
        this.currentTime >= this.totalDuration - 0.05;
      // Sometimes the trimmed-out advance logic leaves the active source past
      // its clip-out point on a single-clip cut — same end-state symptom.
      const elAtSourceEnd =
        !!el.duration &&
        isFinite(el.duration) &&
        el.currentTime >= el.duration - 0.05;
      if (atEnd || (this.currentIndex >= this.videoUrls.length - 1 && elAtSourceEnd)) {
        this.replayFromStart();
        return;
      }
      this.playEl(el);
      this.scheduleRvfc(el);
      // (Re-)play might land on an unavailable clip — kick the auto-skip
      // timer in that case so we don't sit stuck on the overlay.
      this.isPlaying = true;
      this.scheduleUnavailableSkipIfNeeded();
    } else {
      this.stopPlayback();
    }
  }

  /** Stop playing, whether a <video> is driving the slot or the file-less
   *  timer is. The `pause` event only arrives for a real file, so a drawn
   *  slot has to flip the flag and tell the page itself. */
  private stopPlayback(): void {
    const el = this.activeEl;
    if (el && !el.paused) {
      el.pause(); // onPause() flips isPlaying and emits
    } else if (this.isPlaying) {
      this.isPlaying = false;
      this.videoPause.emit();
    }
    this.syncVo();
    this.clearUnavailableSkip();
  }

  /** Reset the timeline to global 0 (first clip's in-point) and start playing.
   *  Handles both the "already on clip 0" fast path and the "on a later clip"
   *  case where we need to swap the slot URL before the active <video>
   *  element points at clip 0. Used by togglePlay() when the user hits play
   *  after the cut has ended. */
  private replayFromStart(): void {
    // Fast path — we never advanced past clip 0 (single-clip cut or user
    // never let it move on). Just rewind the active element and play.
    if (this.currentIndex === 0) {
      const el = this.activeEl;
      if (!el) return;
      try {
        el.currentTime = this.getClipInTime(0);
        el.playbackRate = this.playbackRate;
      } catch {}
      this.currentTime = 0;
      this.progress = 0;
      this.playEl(el);
      this.scheduleRvfc(el);
      this.syncVo();
      this.isPlaying = true;
      this.scheduleUnavailableSkipIfNeeded();
      return;
    }
    // Slow path — we're on a later clip (typical "video ended" case).
    // Swap back to slot A with clip 0 + clip 1 preloaded, wait for metadata,
    // then seek to the clip's in-point and play.
    this.activeSlot = "A";
    this.currentIndex = 0;
    this.slotAUrl = this.videoUrls[0] ?? null;
    this.slotBUrl = this.videoUrls[1] ?? null;
    this.currentTime = 0;
    this.progress = 0;
    queueMicrotask(() => {
      const el = this.activeEl;
      if (!el) return;
      const start = () => {
        try {
          el.currentTime = this.getClipInTime(0);
          el.playbackRate = this.playbackRate;
        } catch {}
        this.playEl(el);
        this.scheduleRvfc(el);
        this.syncVo();
        this.isPlaying = true;
        this.scheduleUnavailableSkipIfNeeded();
      };
      // If the source already has metadata (e.g. slot A still holding clip 0
      // from a previous play) we can start straight away. Otherwise wait for
      // loadedmetadata so currentTime is settable.
      if (el.readyState >= 1) {
        start();
      } else {
        const onReady = () => {
          el.removeEventListener("loadedmetadata", onReady);
          start();
        };
        el.addEventListener("loadedmetadata", onReady);
      }
    });
  }

  toggleFullscreen(): void {
    const el = this.videoWrapperRef?.nativeElement;
    if (!el) return;
    if (this.getFullscreenElement()) {
      this.exitFullscreen();
    } else {
      this.requestFullscreen(el);
    }
  }

  toggleMute(): void {
    this.isMuted = !this.isMuted;
    this.refreshAllVolumes();
  }

  toggleTimelineMarkers(): void {
    this.showTimelineMarkers = !this.showTimelineMarkers;
  }

  /** Keyboard shortcut: `M` flips the clip/VO marker overlay. Ignored
   *  while the user is typing in an input (overlay form, etc.) so we
   *  don't hijack normal text entry. */
  @HostListener("document:keydown", ["$event"])
  onDocumentKeyDown(event: KeyboardEvent): void {
    if (event.defaultPrevented) return;
    const target = event.target as HTMLElement | null;
    const tag = target?.tagName;
    if (
      tag === "INPUT" ||
      tag === "TEXTAREA" ||
      tag === "SELECT" ||
      target?.isContentEditable
    ) {
      return;
    }
    if (event.key === "m" || event.key === "M") {
      this.toggleTimelineMarkers();
    }
  }

  onVolumeInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const v = parseFloat(target.value);
    if (!Number.isFinite(v)) return;
    this.masterVolume = Math.max(0, Math.min(1, v));
    if (this.isMuted && this.masterVolume > 0) this.isMuted = false;
    this.refreshAllVolumes();
  }

  stepVolume(delta: number): void {
    const next = Math.max(0, Math.min(1, this.masterVolume + delta));
    this.masterVolume = Math.round(next * 100) / 100;
    if (this.isMuted && this.masterVolume > 0) this.isMuted = false;
    this.refreshAllVolumes();
  }

  get volumeIcon(): string {
    if (this.isMuted || this.masterVolume === 0) return "🔇";
    if (this.masterVolume < 0.34) return "🔈";
    if (this.masterVolume < 0.67) return "🔉";
    return "🔊";
  }

  private effectiveMaster(): number {
    return this.isMuted ? 0 : this.masterVolume;
  }

  private refreshAllVolumes(): void {
    this.applyDucking();
    this.syncBgm();
    const mul = this.effectiveMaster();
    for (const vo of this.voRuntimes) {
      if (vo.el && vo.active) {
        try {
          vo.el.volume = (vo.track.volume ?? 1) * mul;
        } catch {}
      }
    }
  }

  cyclePlaybackRate(): void {
    const idx = this.playbackRates.indexOf(this.playbackRate);
    const next =
      this.playbackRates[(idx + 1) % this.playbackRates.length] ?? 1;
    this.setPlaybackRate(next);
  }

  setPlaybackRate(rate: number): void {
    this.playbackRate = rate;
    const a = this.videoARef?.nativeElement;
    const b = this.videoBRef?.nativeElement;
    if (a) a.playbackRate = rate;
    if (b) b.playbackRate = rate;
    for (const vo of this.voRuntimes) {
      if (vo.el && vo.active) vo.el.playbackRate = rate;
    }
    for (const bgm of this.bgmRuntimes) {
      if (bgm.el) bgm.el.playbackRate = rate;
    }
  }

  @HostListener("document:fullscreenchange")
  @HostListener("document:webkitfullscreenchange")
  @HostListener("document:msfullscreenchange")
  onFullscreenChange(): void {
    this.isFullscreen = !!this.getFullscreenElement();
    // The wrapper has not been laid out at its new size yet, so measure on
    // the frames after this one rather than on this one.
    requestAnimationFrame(() => this.recomputeOverlayScale());
    setTimeout(() => this.recomputeOverlayScale(), 250);
  }

  private getFullscreenElement(): Element | null {
    const d = document as any;
    return (
      d.fullscreenElement ||
      d.webkitFullscreenElement ||
      d.msFullscreenElement ||
      null
    );
  }

  private requestFullscreen(el: HTMLElement): void {
    const anyEl = el as any;
    const fn =
      el.requestFullscreen ||
      anyEl.webkitRequestFullscreen ||
      anyEl.msRequestFullscreen;
    if (fn) fn.call(el).catch?.(() => {});
  }

  private exitFullscreen(): void {
    const d = document as any;
    const fn =
      document.exitFullscreen || d.webkitExitFullscreen || d.msExitFullscreen;
    if (fn) fn.call(document).catch?.(() => {});
  }

  onPlay(slot: Slot): void {
    if (slot !== this.activeSlot) return;
    if (!this.isPlaying) {
      this.isPlaying = true;
      this.videoPlay.emit();
    }
    this.scheduleUnavailableSkipIfNeeded();
  }

  onPause(slot: Slot): void {
    if (slot !== this.activeSlot) return;
    if (this.isPlaying) {
      this.isPlaying = false;
      this.videoPause.emit();
    }
    this.clearUnavailableSkip();
  }

  onTimeUpdate(slot: Slot): void {
    if (slot !== this.activeSlot) return;
    // If we've crossed the trimmed-out point, advance to the next clip
    // early — exactly the way Premiere/Resolve cut a clip at the out
    // mark instead of letting the source play through. Tiny tolerance so
    // a frame just shy of out doesn't bounce.
    const el = this.activeEl;
    const out = this.getClipOutTime(this.currentIndex);
    if (
      el &&
      out > 0 &&
      this.clipRawDurations[this.currentIndex] > 0 &&
      el.currentTime >= out - 0.02
    ) {
      this.advanceToNext();
      return;
    }
    this.currentTime = this.computeCurrentTime();
    if (this.totalDuration > 0) {
      this.progress = (this.currentTime / this.totalDuration) * 100;
    }
    this.syncVo();
    // `timeupdate` only fires a few times a second, so a fade's sound is
    // stepped rather than smooth on its own — the rVFC loop below fills
    // in the frames while a clip is playing.
    this.tickDip();
    this.videoTimeUpdate.emit(this.currentTime);
  }

  onEnded(slot: Slot): void {
    if (slot !== this.activeSlot) return;
    if (this.currentIndex >= this.videoUrls.length - 1) {
      // Natural end-of-stream — flip the flag and emit pause so the
      // parent's UI bindings (e.g. the toolbar's "Save edits before
      // download" gate) see the same paused state any other pause path
      // would produce. Without the emit, isPlaying-driven controls
      // updated but rxjs-driven ones didn't.
      if (this.isPlaying) {
        this.isPlaying = false;
        this.videoPause.emit();
      }
      this.clearUnavailableSkip();
      return;
    }
    this.advanceToNext();
  }

  private advanceToNext(): void {
    // End-of-stream guard. onEnded already handles the natural-end case,
    // but onTimeUpdate's trimmed-out detection ALSO calls advanceToNext —
    // and on the LAST clip that would push currentIndex past the URL
    // list, leaving the player in a stale "playing nothing" state with
    // isPlaying stuck true (so the controls keep showing ⏸ instead of ▶).
    // Mirror onEnded's behavior here: pause + flip the flag, fire the
    // pause emitter, and bail.
    if (this.currentIndex >= this.videoUrls.length - 1) {
      const el = this.activeEl;
      if (el) try { el.pause(); } catch {}
      if (this.isPlaying) {
        this.isPlaying = false;
        this.videoPause.emit();
      }
      this.clearUnavailableSkip();
      return;
    }
    const newSlot: Slot = this.activeSlot === "A" ? "B" : "A";
    // A blended join has been playing the incoming clip in the spare slot
    // so that it arrives on its own in-point exactly now. Seeking it back
    // would stutter the picture, so note that and leave it be.
    const prerolled = this.blendEngaged && this.blendPrerollTime !== null;
    this.clearBlend();
    this.currentIndex++;
    this.activeSlot = newSlot;
    // Point the now-hidden slot at the clip after this one here, not in the
    // microtask below: it only needs `activeSlot`, and changing a `[src]`
    // binding after the view has been checked is what raises NG0100 when
    // the slot we just left was a title card (its URL was empty).
    this.updatePreloadSlot();

    queueMicrotask(() => {
      const el = this.activeEl;
      if (el) {
        const inPoint = this.getClipInTime(this.currentIndex);
        try {
          // Only seek when the clip isn't already sitting where it should
          // be — after a blend it is, to within a frame or two.
          if (!prerolled || Math.abs(el.currentTime - inPoint) > 0.2) {
            el.currentTime = inPoint;
          }
          el.playbackRate = this.playbackRate;
        } catch {}
        this.playEl(el);
        this.scheduleRvfc(el);
      }
      this.syncVo();
      // After the new clip is mounted, decide whether we need the auto-skip
      // timer (only fires when the new clip is unavailable — its <video>
      // can't drive `ended` so we time it out manually).
      this.scheduleUnavailableSkipIfNeeded();
    });
  }

  // ─── Unavailable-clip auto-skip (timer-driven) ──────────────────────────
  // For clips marked is_available=false the signed URL is often 404; the
  // <video> element never loads, never fires timeupdate, and `ended` never
  // arrives — so the player would stall on the unavailable slot forever.
  // We bridge that by scheduling a setTimeout for the clip's trimmed
  // duration; when it fires we advance like a normal end-of-clip would.
  // The timer is canceled on pause / seek / destroy and re-scheduled on
  // each (re-)play so unavailable clips honor pause and scrub like any
  // other clip.
  private unavailableSkipTimer: ReturnType<typeof setTimeout> | null = null;
  private unavailableSkipStartedAt: number = 0;
  private unavailableSkipDurationMs: number = 0;

  private clearUnavailableSkip(): void {
    if (this.unavailableSkipTimer != null) {
      clearTimeout(this.unavailableSkipTimer);
      this.unavailableSkipTimer = null;
    }
    this.unavailableSkipStartedAt = 0;
    this.unavailableSkipDurationMs = 0;
  }

  private scheduleUnavailableSkipIfNeeded(): void {
    this.clearUnavailableSkip();
    // Only run when nothing can play this slot — a missing file or a drawn
    // title card — AND the player is in a playing state. Pause + seek call
    // clearUnavailableSkip explicitly.
    if (!this.isCurrentSlotTimerDriven) return;
    if (!this.isPlaying) return;
    const visibleDur =
      this.getClipOutTime(this.currentIndex) -
      this.getClipInTime(this.currentIndex);
    if (!(visibleDur > 0)) {
      // Zero-length unavailable clip → skip on the next tick rather than
      // tight-looping setTimeout(0).
      this.unavailableSkipTimer = setTimeout(() => this.onUnavailableSkipFire(), 16);
      return;
    }
    this.unavailableSkipDurationMs = Math.round(visibleDur * 1000 / Math.max(0.01, this.playbackRate));
    this.unavailableSkipStartedAt = performance.now();
    this.unavailableSkipTimer = setTimeout(
      () => this.onUnavailableSkipFire(),
      this.unavailableSkipDurationMs,
    );
    // Drive the global currentTime forward via rAF so the scrub bar reflects
    // progress through the unavailable slot — otherwise the overlay would
    // sit with a frozen timecode for the clip's full duration.
    this.tickUnavailableProgress();
  }

  private onUnavailableSkipFire(): void {
    this.clearUnavailableSkip();
    if (this.currentIndex >= this.videoUrls.length - 1) {
      // Was the last clip — mirror the natural end-of-stream behavior.
      this.isPlaying = false;
      const el = this.activeEl;
      if (el) try { el.pause(); } catch {}
      return;
    }
    this.advanceToNext();
  }

  private tickUnavailableProgress(): void {
    if (this.unavailableSkipTimer == null) return;
    const elapsed = performance.now() - this.unavailableSkipStartedAt;
    const fracDone = Math.min(1, elapsed / Math.max(1, this.unavailableSkipDurationMs));
    const dur =
      this.getClipOutTime(this.currentIndex) -
      this.getClipInTime(this.currentIndex);
    const localElapsed = fracDone * dur;
    const offset = this.cumulativeOffsets[this.currentIndex] ?? 0;
    this.currentTime = offset + localElapsed;
    if (this.totalDuration > 0) {
      this.progress = (this.currentTime / this.totalDuration) * 100;
    }
    this.tickDip();
    // Effects have to be checked here too: this is the only clock running
    // while a title card is on screen, and a sound placed over a card is
    // an ordinary thing to want (a sting under an end card).
    this.syncSfx();
    this.videoTimeUpdate.emit(this.currentTime);
    if (fracDone < 1 && this.unavailableSkipTimer != null) {
      requestAnimationFrame(() => this.tickUnavailableProgress());
    }
  }

  private updatePreloadSlot(): void {
    const nextUrl = this.videoUrls[this.currentIndex + 1] ?? null;
    if (this.activeSlot === "A") {
      if (this.slotBUrl !== nextUrl) this.slotBUrl = nextUrl;
    } else {
      if (this.slotAUrl !== nextUrl) this.slotAUrl = nextUrl;
    }
  }

  seek(event: MouseEvent): void {
    if (!this.metadataReady || this.totalDuration <= 0) return;
    const bar = event.currentTarget as HTMLElement;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(
      0,
      Math.min(1, (event.clientX - rect.left) / rect.width),
    );
    const globalTime = pct * this.totalDuration;
    this.seekToGlobal(globalTime);
  }

  // ─── Public imperative API ────────────────────────────────────────────
  // Mirrors the surface area of the videojs player (play/pause/seekTo/
  // currentTime/paused) so the workspace component can drive compare-mode
  // sync without caring which player is mounted.

  /** Seek to a timeline-global time in seconds. Clamped to [0, totalDuration]. */
  seekTo(seconds: number): void {
    if (!this.metadataReady || this.totalDuration <= 0) return;
    // Land inside a fade and the picture should already be dipped when the
    // frame arrives, so repaint once the seek has settled.
    queueMicrotask(() => this.tickDip());
    const clamped = Math.max(0, Math.min(this.totalDuration, seconds));
    // Drop any in-flight auto-skip timer; the new slot decides whether to
    // restart one (handled by seekToGlobal → scheduleUnavailableSkipIfNeeded).
    this.clearUnavailableSkip();
    this.seekToGlobal(clamped);
    this.videoSeeked.emit(clamped);
    this.scheduleUnavailableSkipIfNeeded();
  }

  /** Start playback (no-op if already playing). */
  play(): void {
    const el = this.activeEl;
    if (!el) return;
    if (el.paused) {
      this.playEl(el);
      this.scheduleRvfc(el);
    }
    this.isPlaying = true;
    this.scheduleUnavailableSkipIfNeeded();
  }

  /** Pause playback (no-op if already paused). */
  pause(): void {
    this.stopPlayback();
  }

  /** Current timeline-global time in seconds. */
  getCurrentTime(): number {
    return this.currentTime;
  }

  /** True when the active slot is paused. A drawn slot has no <video> to
   *  ask — its element is always paused — so go by our own flag there. */
  paused(): boolean {
    if (this.isCurrentSlotTimerDriven) return !this.isPlaying;
    const el = this.activeEl;
    return el ? el.paused : true;
  }

  /** Annotation pin position as a percent of total duration. */
  getAnnotationPercent(a: VideoAnnotation): number {
    return this.totalDuration > 0 ? (a.time / this.totalDuration) * 100 : 0;
  }

  /**
   * Compact relative time for the overlay label chip
   * — "just now", "5m ago", "3h ago", "2d ago", or a date string for older.
   */
  formatOverlayCreatedAt(iso: string | null | undefined): string {
    if (!iso) return "";
    const ts = new Date(iso).getTime();
    if (!Number.isFinite(ts)) return "";
    const diff = Math.max(0, Date.now() - ts);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
  }

  /** Click handler for annotation pins on the timeline. */
  onAnnotationClick(event: MouseEvent, annotation: VideoAnnotation): void {
    event.stopPropagation();
    this.seekTo(annotation.time);
    this.annotationClick.emit(annotation);
  }

  // ─── Spatial overlays (annotations drawn on the video frame) ─────────

  /** Overlays whose [startTime, endTime] window contains the current time. */
  get visibleOverlays(): VideoOverlay[] {
    const t = this.currentTime;
    return this.overlays.filter(
      (o) => t >= o.startTime && t <= o.endTime,
    );
  }

  /**
   * True when the current viewer is allowed to edit / delete / move this
   * overlay. Legacy overlays with no `createdBy` stay editable by anyone
   * (used by the standalone demo). When ownership checks are enabled
   * (`currentUserId` is set), only the author can modify their own pin.
   */
  canEdit(overlay: VideoOverlay): boolean {
    if (overlay.createdBy == null) return true;
    if (this.currentUserId == null) return false;
    return String(overlay.createdBy) === String(this.currentUserId);
  }

  /** Click handler for a saved overlay shape on the video surface. */
  onOverlayClick(event: MouseEvent, overlay: VideoOverlay): void {
    if (this.isAddingOverlay) return;
    event.stopPropagation();
    // Swallow the click that completes a drag — mousedown + drag + mouseup
    // fires a synthetic click event, which would otherwise seek to the
    // overlay's startTime and feel "jumpy".
    if (this.overlayDragMoved) {
      this.overlayDragMoved = false;
      return;
    }
    this.overlayClick.emit(overlay);
  }

  /** × button on a saved overlay — removes it without triggering seek. */
  onOverlayDelete(event: MouseEvent, overlay: VideoOverlay): void {
    event.stopPropagation();
    if (!this.canEdit(overlay)) return;
    this.overlayDelete.emit(overlay);
  }

  /**
   * Scroll-wheel resize on a saved overlay. Scroll up = grow, down =
   * shrink. Page scroll is suppressed so the video viewport stays put.
   * Emits a debounced overlayUpdate so callers can persist the final
   * size without 60 events per second.
   */
  private readonly OVERLAY_SIZE_MIN = 20;
  private readonly OVERLAY_SIZE_MAX = 150;
  private readonly OVERLAY_SIZE_STEP = 5;
  private overlayResizeEmitHandle: number | null = null;
  private overlayResizeEmitTarget: VideoOverlay | null = null;

  onOverlayWheel(event: WheelEvent, overlay: VideoOverlay): void {
    if (this.isAddingOverlay || this.showOverlayForm) return;
    if (!this.canEdit(overlay)) return;
    event.preventDefault();
    event.stopPropagation();
    const current = overlay.size ?? 40;
    const direction = event.deltaY < 0 ? 1 : -1;
    const next = Math.max(
      this.OVERLAY_SIZE_MIN,
      Math.min(
        this.OVERLAY_SIZE_MAX,
        current + direction * this.OVERLAY_SIZE_STEP,
      ),
    );
    if (next === current) return;
    overlay.size = next;
    // Debounce the emit — pile up resize events and only fire once the
    // user stops scrolling for 200ms.
    this.overlayResizeEmitTarget = overlay;
    if (this.overlayResizeEmitHandle !== null) {
      clearTimeout(this.overlayResizeEmitHandle);
    }
    this.overlayResizeEmitHandle = window.setTimeout(() => {
      this.overlayResizeEmitHandle = null;
      if (this.overlayResizeEmitTarget) {
        this.overlayUpdate.emit(this.overlayResizeEmitTarget);
        this.overlayResizeEmitTarget = null;
      }
    }, 200);
  }

  /**
   * Begin a drag-reposition on an existing overlay. We don't decide it's
   * an actual drag until the mouse moves more than OVERLAY_DRAG_THRESHOLD_PX
   * — under that it stays a "click" and triggers seek as before.
   */
  onOverlayMouseDown(event: MouseEvent, overlay: VideoOverlay): void {
    if (this.isAddingOverlay || this.showOverlayForm) return;
    // Only the author can drag-move their pin. Click-to-seek still works
    // for everyone because that's handled in onOverlayClick below.
    if (!this.canEdit(overlay)) return;
    // Ignore right-clicks and clicks that started on the × delete or pencil
    // edit buttons.
    if (event.button !== 0) return;
    const target = event.target as HTMLElement;
    if (
      target.closest(".overlay-delete-btn") ||
      target.closest(".overlay-edit-btn")
    ) {
      return;
    }

    event.preventDefault();
    this.draggingOverlayId = overlay.id;
    this.draggedOverlay = overlay;
    this.overlayDragMoved = false;
    this.overlayDragStart = {
      mouseX: event.clientX,
      mouseY: event.clientY,
      x: overlay.x,
      y: overlay.y,
    };
  }

  /**
   * Enter/exit "click on the video to place an annotation" mode. While
   * active, the cursor turns to crosshair and the next click on the
   * video surface opens the overlay form pre-populated with that
   * position and the current timecode.
   *
   * Entering pauses playback so the user can pin precisely on the frame
   * they're looking at; exiting does NOT auto-resume — the user clicks
   * play themselves when they're done.
   */
  toggleAddOverlayMode(): void {
    if (this.readOnly) return;
    if (this.isAddingOverlay) {
      this.cancelOverlayCreation();
    } else {
      this.isAddingOverlay = true;
      this.showOverlayForm = false;
      this.pause();
    }
  }

  /**
   * Click on the video surface while in add-overlay mode. Ignores clicks
   * that bubbled from controls/form/indicator so they keep working
   * normally — only direct hits on the video element or its wrapper drop
   * a pin.
   */
  onVideoSurfaceClick(event: MouseEvent): void {
    if (!this.isAddingOverlay || this.showOverlayForm) return;
    const target = event.target as HTMLElement;
    const isVideoSurface =
      target.classList.contains("video-element") ||
      target.classList.contains("video-wrapper");
    if (!isVideoSurface) return;

    const wrapper = event.currentTarget as HTMLElement;
    const rect = wrapper.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    const now = Math.floor(this.currentTime);
    const totalCap = this.totalDuration > 0 ? this.totalDuration : now + 5;
    this.newOverlay = {
      id: Date.now(),
      x: Math.round(x * 10) / 10,
      y: Math.round(y * 10) / 10,
      startTime: now,
      endTime: Math.min(totalCap, now + 5),
      shape: "circle",
      color: this.overlayColors[0],
      size: 50,
      label: "",
    };
    this.showOverlayForm = true;
  }

  saveOverlay(): void {
    if (this.newOverlay.x === undefined || this.newOverlay.y === undefined) {
      return;
    }

    // EDIT path — mutate the existing overlay in place so callers using
    // the same array reference see the change, then emit overlayUpdate.
    if (this.editingOverlayId !== null) {
      const original = this.overlays.find(
        (o) => o.id === this.editingOverlayId,
      );
      if (original) {
        original.startTime = this.newOverlay.startTime ?? original.startTime;
        original.endTime = this.newOverlay.endTime ?? original.endTime;
        original.x = this.newOverlay.x;
        original.y = this.newOverlay.y;
        original.shape = this.newOverlay.shape ?? original.shape;
        original.color = this.newOverlay.color ?? original.color;
        original.size = this.newOverlay.size ?? original.size;
        original.label = this.newOverlay.label ?? "";
        this.overlayUpdate.emit(original);
      }
      this.cancelOverlayCreation();
      return;
    }

    // CREATE path
    const overlay: VideoOverlay = {
      id: this.newOverlay.id ?? Date.now(),
      startTime: this.newOverlay.startTime ?? this.currentTime,
      endTime: this.newOverlay.endTime ?? this.currentTime + 5,
      x: this.newOverlay.x,
      y: this.newOverlay.y,
      shape: this.newOverlay.shape ?? "circle",
      color: this.newOverlay.color ?? this.overlayColors[0],
      size: this.newOverlay.size ?? 50,
      label: this.newOverlay.label ?? "",
    };
    this.overlayAdd.emit(overlay);
    this.cancelOverlayCreation();
  }

  cancelOverlayCreation(): void {
    this.showOverlayForm = false;
    this.newOverlay = {};
    this.isAddingOverlay = false;
    this.editingOverlayId = null;
    this.formPos = null;
  }

  /**
   * Pencil button on a saved overlay — open the create form pre-populated
   * with the overlay's current values for in-place editing. Pauses
   * playback so the form values don't desync from a moving timeline.
   */
  onOverlayEdit(event: MouseEvent, overlay: VideoOverlay): void {
    event.stopPropagation();
    if (!this.canEdit(overlay)) return;
    this.pause();
    this.editingOverlayId = overlay.id;
    this.isAddingOverlay = false;
    this.newOverlay = {
      id: overlay.id,
      x: overlay.x,
      y: overlay.y,
      startTime: overlay.startTime,
      endTime: overlay.endTime,
      shape: overlay.shape,
      color: overlay.color,
      size: overlay.size,
      label: overlay.label,
    };
    this.showOverlayForm = true;
  }

  pickOverlayShape(shape: VideoOverlay["shape"]): void {
    this.newOverlay.shape = shape;
  }

  pickOverlayColor(color: string): void {
    this.newOverlay.color = color;
  }

  // ─── Draggable creation panel ─────────────────────────────────────────

  /** Start dragging the overlay form by its header. */
  onFormDragStart(event: MouseEvent): void {
    event.preventDefault();
    const wrapper = this.videoWrapperRef?.nativeElement;
    if (!wrapper) return;
    const wrapRect = wrapper.getBoundingClientRect();
    // If this is the first drag, materialize the current docked position
    // into absolute left/top so the panel doesn't jump.
    if (!this.formPos) {
      const formEl = (event.currentTarget as HTMLElement).closest(
        ".overlay-form",
      ) as HTMLElement | null;
      if (formEl) {
        const formRect = formEl.getBoundingClientRect();
        this.formPos = {
          left: formRect.left - wrapRect.left,
          top: formRect.top - wrapRect.top,
        };
      } else {
        this.formPos = { left: 12, top: 12 };
      }
    }
    this.dragging = true;
    this.dragStart = {
      mouseX: event.clientX,
      mouseY: event.clientY,
      left: this.formPos.left,
      top: this.formPos.top,
    };
  }

  @HostListener("document:mousemove", ["$event"])
  onDocumentMouseMove(event: MouseEvent): void {
    // Creation-panel drag
    if (this.dragging && this.formPos) {
      const wrapper = this.videoWrapperRef?.nativeElement;
      if (!wrapper) return;
      const wrapRect = wrapper.getBoundingClientRect();
      const dx = event.clientX - this.dragStart.mouseX;
      const dy = event.clientY - this.dragStart.mouseY;
      const PANEL_W = 240;
      const left = Math.max(
        0,
        Math.min(wrapRect.width - PANEL_W, this.dragStart.left + dx),
      );
      const top = Math.max(
        0,
        Math.min(wrapRect.height - 40, this.dragStart.top + dy),
      );
      this.formPos = { left, top };
      return;
    }

    // Overlay reposition drag
    if (this.draggingOverlayId !== null && this.draggedOverlay) {
      const wrapper = this.videoWrapperRef?.nativeElement;
      if (!wrapper) return;
      const dxPx = event.clientX - this.overlayDragStart.mouseX;
      const dyPx = event.clientY - this.overlayDragStart.mouseY;
      if (
        !this.overlayDragMoved &&
        Math.hypot(dxPx, dyPx) < this.OVERLAY_DRAG_THRESHOLD_PX
      ) {
        return; // still inside the dead zone — treat as click
      }
      this.overlayDragMoved = true;
      const wrapRect = wrapper.getBoundingClientRect();
      // Convert pixel delta back to percentage relative to the wrapper.
      const dxPct = (dxPx / wrapRect.width) * 100;
      const dyPct = (dyPx / wrapRect.height) * 100;
      this.draggedOverlay.x = Math.max(
        0,
        Math.min(100, this.overlayDragStart.x + dxPct),
      );
      this.draggedOverlay.y = Math.max(
        0,
        Math.min(100, this.overlayDragStart.y + dyPct),
      );
    }
  }

  @HostListener("document:mouseup")
  onDocumentMouseUp(): void {
    this.dragging = false;
    if (this.draggingOverlayId !== null) {
      const moved = this.overlayDragMoved;
      const overlay = this.draggedOverlay;
      this.draggingOverlayId = null;
      this.draggedOverlay = null;
      if (moved && overlay) {
        this.overlayUpdate.emit(overlay);
      }
      // overlayDragMoved is cleared inside onOverlayClick, which fires next
      // due to the synthetic click after mousedown+mouseup on the same el.
    }
  }

  skip(deltaSeconds: number): void {
    if (!this.metadataReady) return;
    const target = Math.max(
      0,
      Math.min(this.totalDuration, this.currentTime + deltaSeconds),
    );
    this.seekToGlobal(target);
  }

  stop(): void {
    const el = this.activeEl;
    if (el) el.pause();
    this.pauseAllVoAudio();
    this.pauseAllBgmAudio();
    if (this.currentIndex === 0) {
      if (el) {
        try {
          el.currentTime = this.getClipInTime(0);
        } catch {}
        this.onTimeUpdate(this.activeSlot);
      }
      return;
    }
    this.activeSlot = "A";
    this.currentIndex = 0;
    this.slotAUrl = this.videoUrls[0] ?? null;
    this.slotBUrl = this.videoUrls[1] ?? null;
    this.currentTime = 0;
    this.progress = 0;
    this.seekActiveSlotToInPoint();
  }

  private seekToGlobal(globalTime: number): void {
    // Whatever was sounding belonged to where the playhead WAS.
    this.stopAllSfx();
    // Whatever the spare slot was doing for a blend, it is out of date the
    // moment the playhead jumps; the next tick starts it again if needed.
    if (this.blendEngaged) this.clearBlend();
    // Take the new time now rather than in the microtasks below. They run
    // after change detection has already checked the timecode against the
    // old value, which is an NG0100 in dev; the refinement they make is to
    // the same number anyway.
    this.currentTime = globalTime;
    if (this.totalDuration > 0) {
      this.progress = (globalTime / this.totalDuration) * 100;
    }
    const idx = this.findClipForGlobalTime(globalTime);
    // localTrimmed is offset within the trimmed window; localSource adds
    // the clip's in-point so we land at the right frame of the source.
    const localTrimmed = globalTime - (this.cumulativeOffsets[idx] ?? 0);
    const localSource = this.getClipInTime(idx) + Math.max(0, localTrimmed);
    const el = this.activeEl;

    if (idx === this.currentIndex) {
      if (el) {
        el.currentTime = localSource;
        this.onTimeUpdate(this.activeSlot);
        this.scheduleRvfc(el);
      }
      return;
    }

    if (idx === this.currentIndex + 1) {
      const wasPlaying = !!el && !el.paused;
      const newSlot: Slot = this.activeSlot === "A" ? "B" : "A";
      this.currentIndex = idx;
      this.activeSlot = newSlot;
      // Same reason as in advanceToNext(): set the preload slot's URL while
      // we are still inside change detection.
      this.updatePreloadSlot();
      queueMicrotask(() => {
        const nextEl = this.activeEl;
        if (nextEl) {
          try {
            nextEl.currentTime = localSource;
            nextEl.playbackRate = this.playbackRate;
          } catch {}
          // `wasPlaying` is what the element was doing before the jump;
          // `isPlaying` covers a play() asked for straight after it, which
          // is what pressing play after a scrub looks like.
          if (wasPlaying || this.isPlaying) {
            this.playEl(nextEl);
            this.scheduleRvfc(nextEl);
          }
        }
        this.currentTime = this.computeCurrentTime();
        this.syncVo();
      });
      return;
    }

    const wasPlaying = !!el && !el.paused;
    this.currentIndex = idx;
    const targetUrl = this.videoUrls[idx] ?? null;
    const nextUrl = this.videoUrls[idx + 1] ?? null;
    if (this.activeSlot === "A") {
      this.slotAUrl = targetUrl;
      this.slotBUrl = nextUrl;
    } else {
      this.slotBUrl = targetUrl;
      this.slotAUrl = nextUrl;
    }
    queueMicrotask(() => {
      const nextEl = this.activeEl;
      if (!nextEl) return;
      const onReady = () => {
        try {
          nextEl.currentTime = localSource;
          nextEl.playbackRate = this.playbackRate;
        } catch {}
        if (wasPlaying || this.isPlaying) {
          this.playEl(nextEl);
          this.scheduleRvfc(nextEl);
        }
        this.currentTime = this.computeCurrentTime();
        this.syncVo();
        nextEl.removeEventListener("loadedmetadata", onReady);
      };
      nextEl.addEventListener("loadedmetadata", onReady);
    });
  }

  private findClipForGlobalTime(t: number): number {
    if (t <= 0) return 0;
    for (let i = this.videoUrls.length - 1; i >= 0; i--) {
      if (t >= (this.cumulativeOffsets[i] ?? 0)) return i;
    }
    return 0;
  }

  formatTime(seconds: number): string {
    if (!seconds || isNaN(seconds)) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  }
}
